import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import { supabase } from './supabase-client';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { Menu, LogOut, Home, Users, DollarSign, ShoppingCart, X, Plus, Check, Award, TrendingUp, TrendingDown, Receipt as ReceiptIcon, ChevronDown, ChevronRight, Wallet, Clock, Printer, Calendar } from 'lucide-react';

// ========== HARDCODE DATA HISTORICAL ==========
const HARDCODE_DAILY = {
  // --- DATA BULAN AGUSTUS 2026 ---
  '2026-08-01': { revenue: 3040000, pax: 5, cash: 560000, transfer: 2480000 },
  '2026-08-02': { revenue: 3140000, pax: 5, cash: 1150000, transfer: 1990000 },
  '2026-08-03': { revenue: 3900000, pax: 6, cash: 720000, transfer: 3180000 },
  '2026-08-04': { revenue: 3925000, pax: 5, cash: 635000, transfer: 3290000 },
  '2026-08-05': { revenue: 4660000, pax: 8, cash: 1150000, transfer: 3510000 },
  '2026-08-06': { revenue: 5860000, pax: 8, cash: 1120000, transfer: 4740000 },
  '2026-08-07': { revenue: 5840000, pax: 9, cash: 1310000, transfer: 4530000 },
  '2026-08-08': { revenue: 9020000, pax: 11, cash: 1860000, transfer: 7160000 },
  '2026-08-09': { revenue: 7210000, pax: 12, cash: 3070000, transfer: 4140000 },
  '2026-08-10': { revenue: 3950000, pax: 6, cash: 1370000, transfer: 2580000 },
  '2026-08-11': { revenue: 11240000, pax: 18, cash: 0, transfer: 11240000 },
  '2026-08-12': { revenue: 7410000, pax: 12, cash: 560000, transfer: 6850000 },
  '2026-08-13': { revenue: 7850000, pax: 13, cash: 2270000, transfer: 5580000 },
  '2026-08-14': { revenue: 3740000, pax: 6, cash: 560000, transfer: 3180000 },
  '2026-08-15': { revenue: 8225000, pax: 12, cash: 0, transfer: 8225000 },
  '2026-08-16': { revenue: 3270000, pax: 6, cash: 1120000, transfer: 2150000 },
  '2026-08-17': { revenue: 3580000, pax: 6, cash: 590000, transfer: 2990000 },
  '2026-08-18': { revenue: 6970000, pax: 11, cash: 3010000, transfer: 3960000 },
  '2026-08-19': { revenue: 5800000, pax: 9, cash: 1840000, transfer: 3960000 },
  '2026-08-20': { revenue: 4405000, pax: 7, cash: 1740000, transfer: 2665000 },
  '2026-08-21': { revenue: 8714000, pax: 13, cash: 1180000, transfer: 7534000 },
};

const PAYMENT_COLORS = { cash: '#8b5cf6', transfer: '#06b6d4', 'e-wallet': '#f97316' };
const MONTH_NAMES = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const pad2 = (n) => String(n).padStart(2, '0');

// Format tanggal ke lokal 'YYYY-MM-DD'
const toLocalDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Format timestamp lokal untuk query
const toLocalRangeStrings = (startLocal, endLocal) => ({
  startStr: `${startLocal.getFullYear()}-${pad2(startLocal.getMonth() + 1)}-${pad2(startLocal.getDate())}T00:00:00`,
  endStr: `${endLocal.getFullYear()}-${pad2(endLocal.getMonth() + 1)}-${pad2(endLocal.getDate())}T23:59:59.999`,
});

const formatObjDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d, 10)} ${MONTH_NAMES[parseInt(m, 10) - 1].substring(0,3)} ${y}`;
};

// =========================================================================
// SIKLUS 1: KALENDER MURNI (Tanggal 1 s/d 30/31)
// Digunakan untuk: Dashboard, Expenses, Laporan Keuangan
// =========================================================================
const getCalendarPeriodRange = (monthIndex, year, startDate, endDate) => {
  if (startDate && endDate) return { startLocal: new Date(`${startDate}T00:00:00`), endLocal: new Date(`${endDate}T23:59:59.999`) };
  if (startDate) return { startLocal: new Date(`${startDate}T00:00:00`), endLocal: new Date(`${startDate}T23:59:59.999`) };
  if (endDate) return { startLocal: new Date(`${endDate}T00:00:00`), endLocal: new Date(`${endDate}T23:59:59.999`) };
  
  return {
    startLocal: new Date(year, monthIndex, 1, 0, 0, 0, 0),
    endLocal: new Date(year, monthIndex + 1, 0, 23, 59, 59, 999), // Hari terakhir bulan tersebut
  };
};

const getCalendarDisplayLabel = (startDate, endDate, monthIndex, year) => {
  if (startDate && endDate) return `${formatObjDate(startDate)} - ${formatObjDate(endDate)}`;
  if (startDate) return formatObjDate(startDate);
  if (endDate) return formatObjDate(endDate);
  return `${MONTH_NAMES[monthIndex]} ${year}`;
};

// =========================================================================
// SIKLUS 2: CUT-OFF GAJIAN (Tanggal 22 bulan lalu s/d 21 bulan ini)
// Digunakan untuk: Gaji Staff & Terapis, Rincian Komisi Harian
// =========================================================================
const PAYROLL_START_DAY = 22;
const PAYROLL_END_DAY = 21;

const getPayrollPeriodRange = (monthIndex, year, startDate, endDate) => {
  if (startDate && endDate) return { startLocal: new Date(`${startDate}T00:00:00`), endLocal: new Date(`${endDate}T23:59:59.999`) };
  if (startDate) return { startLocal: new Date(`${startDate}T00:00:00`), endLocal: new Date(`${startDate}T23:59:59.999`) };
  if (endDate) return { startLocal: new Date(`${endDate}T00:00:00`), endLocal: new Date(`${endDate}T23:59:59.999`) };
  
  const prev = new Date(year, monthIndex - 1, 1);
  return {
    startLocal: new Date(prev.getFullYear(), prev.getMonth(), PAYROLL_START_DAY, 0, 0, 0, 0),
    endLocal: new Date(year, monthIndex, PAYROLL_END_DAY, 23, 59, 59, 999),
  };
};

const getPayrollDisplayLabel = (startDate, endDate, monthIndex, year) => {
  if (startDate && endDate) return `${formatObjDate(startDate)} - ${formatObjDate(endDate)}`;
  if (startDate) return formatObjDate(startDate);
  if (endDate) return formatObjDate(endDate);
  
  let prevMonth = monthIndex - 1;
  let prevYear = year;
  if (prevMonth < 0) {
    prevMonth = 11;
    prevYear -= 1;
  }
  return `22 ${MONTH_NAMES[prevMonth].substring(0,3)} ${prevYear} - 21 ${MONTH_NAMES[monthIndex].substring(0,3)} ${year}`;
};


// ========== CUSTOM DROPDOWNS ==========
function YearDropdown({ value, onChange, yearsBack = 6 }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: yearsBack }, (_, i) => thisYear - i);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border rounded-lg px-4 py-1.5 text-sm bg-gray-50 flex items-center gap-2 hover:bg-white min-w-[80px] justify-between shadow-sm transition-colors"
      >
        {value}
        <ChevronDown size={14} className={`transition-transform text-gray-500 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-lg shadow-lg">
          {years.map(y => (
            <button
              key={y}
              type="button"
              onClick={() => { onChange(y); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${y === value ? 'bg-blue-100 font-semibold text-blue-700' : ''}`}
            >
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [open]);

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border rounded-lg px-4 py-1.5 text-sm bg-gray-50 flex items-center gap-2 hover:bg-white min-w-[110px] justify-between shadow-sm transition-colors"
      >
        {MONTH_NAMES[value]}
        <ChevronDown size={14} className={`transition-transform text-gray-500 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-lg shadow-lg">
          {MONTH_NAMES.map((m, i) => (
            <button
              key={i}
              ref={i === value ? selectedRef : null}
              type="button"
              onClick={() => { onChange(i); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${i === value ? 'bg-blue-100 font-semibold text-blue-700' : ''}`}
            >
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DateRangeDropdown({ startDate, setStartDate, endDate, setEndDate }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);

  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleApply = () => {
    setStartDate(tempStart);
    setEndDate(tempEnd);
    setOpen(false);
  };

  const handleReset = () => {
    setTempStart('');
    setTempEnd('');
    setStartDate('');
    setEndDate('');
    setOpen(false);
  };

  const hasCustomDate = startDate || endDate;

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`border rounded-lg px-4 py-1.5 text-sm flex items-center gap-2 shadow-sm transition-colors ${hasCustomDate ? 'bg-blue-50 border-blue-200 text-blue-700 font-bold' : 'bg-gray-50 hover:bg-white text-gray-700 font-medium'}`}
      >
        <Calendar size={14} className={hasCustomDate ? "text-blue-600" : "text-gray-500"} />
        {hasCustomDate ? "Tgl Custom Aktif" : "Filter Tgl"}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {open && (
        <div className="absolute z-20 mt-2 w-64 p-4 bg-white border rounded-lg shadow-xl right-0 md:left-0 md:right-auto">
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 font-bold mb-1.5 block">Mulai Tanggal</label>
              <input type="date" value={tempStart} onChange={e => setTempStart(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold mb-1.5 block">Sampai Tanggal (Opsional)</label>
              <input type="date" value={tempEnd} onChange={e => setTempEnd(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none" />
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <button onClick={handleReset} className="flex-1 px-3 py-2 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors">Reset</button>
              <button onClick={handleApply} className="flex-1 px-3 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors">Terapkan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// FILTER BAR UNIVERSAL
function FilterBar({ month, setMonth, year, setYear, startDate, setStartDate, endDate, setEndDate, extraButtons }) {
  return (
    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-4">
      <div className="flex flex-wrap items-center gap-2 md:gap-3 bg-white p-2 md:px-3 md:py-2 rounded-lg shadow-sm border w-max">
        
        <div className="flex items-center gap-1.5">
          <MonthDropdown value={month} onChange={setMonth} />
          <YearDropdown value={year} onChange={setYear} />
        </div>

        {setStartDate && (
          <>
            <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <DateRangeDropdown 
                startDate={startDate} setStartDate={setStartDate}
                endDate={endDate} setEndDate={setEndDate} 
              />
            </div>
          </>
        )}
      </div>
      {extraButtons && <div className="flex gap-2">{extraButtons}</div>}
    </div>
  );
}

// ========== MAIN APP ==========
export default function PantiPijatApp() {
  const now = new Date();
  const [globalMonth, setGlobalMonth] = useState(now.getMonth());
  const [globalYear, setGlobalYear] = useState(now.getFullYear());
  const [globalStartDate, setGlobalStartDate] = useState('');
  const [globalEndDate, setGlobalEndDate] = useState('');

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('dashboard');

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [notAuthorized, setNotAuthorized] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAndSetSession(session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      checkAndSetSession(session);
    });

    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      listener.subscription.unsubscribe();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const checkAndSetSession = async (newSession) => {
    if (!newSession) {
      setSession(null);
      setNotAuthorized(false);
      return;
    }
    const { data, error } = await supabase
      .from('allowed_emails')
      .select('email')
      .eq('email', newSession.user.email)
      .maybeSingle();

    if (error || !data) {
      setNotAuthorized(true);
      await supabase.auth.signOut();
      setSession(null);
      return;
    }
    setNotAuthorized(false);
    setSession(newSession);
  };

  const handleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setActivePage('dashboard');
  };

  if (loading) {
    return (
      <div className="w-full h-screen bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4 mx-auto"></div>
          <p className="text-lg font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <LoginPage onLogin={handleLogin} notAuthorized={notAuthorized} />;
  }

  return (
    <>
      <style>{`
        .recharts-wrapper, .recharts-surface, .recharts-wrapper * {
          outline: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }
      `}</style>

      <div className="flex h-screen bg-gray-100">
        <Sidebar
          isOpen={sidebarOpen}
          setIsOpen={setSidebarOpen}
          activePage={activePage}
          setActivePage={setActivePage}
          onLogout={handleLogout}
          userName={session.user.email}
        />

        <div className="flex-1 overflow-auto">
          <Header userName={session.user.email} onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

          <div className="p-6">
            {activePage === 'dashboard' && <Dashboard month={globalMonth} setMonth={setGlobalMonth} year={globalYear} setYear={setGlobalYear} startDate={globalStartDate} setStartDate={setGlobalStartDate} endDate={globalEndDate} setEndDate={setGlobalEndDate} />}
            {activePage === 'therapists' && <TherapistsPage month={globalMonth} setMonth={setGlobalMonth} year={globalYear} setYear={setGlobalYear} startDate={globalStartDate} setStartDate={setGlobalStartDate} endDate={globalEndDate} setEndDate={setGlobalEndDate} />}
            {activePage === 'payroll' && <PayrollPage month={globalMonth} setMonth={setGlobalMonth} year={globalYear} setYear={setGlobalYear} startDate={globalStartDate} setStartDate={setGlobalStartDate} endDate={globalEndDate} setEndDate={setGlobalEndDate} />}
            {activePage === 'expenses' && <ExpensesPage month={globalMonth} setMonth={setGlobalMonth} year={globalYear} setYear={setGlobalYear} startDate={globalStartDate} setStartDate={setGlobalStartDate} endDate={globalEndDate} setEndDate={setGlobalEndDate} />}
            {activePage === 'financial-report' && <FinancialReportPage month={globalMonth} setMonth={setGlobalMonth} year={globalYear} setYear={setGlobalYear} startDate={globalStartDate} setStartDate={setGlobalStartDate} endDate={globalEndDate} setEndDate={setGlobalEndDate} />}
          </div>
        </div>
      </div>
    </>
  );
}

// ========== LOGIN PAGE ==========
function LoginPage({ onLogin, notAuthorized }) {
  return (
    <div className="w-full h-screen bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-600 mb-2">ATLAS Men's Health</h1>
          <p className="text-gray-600">Sistem Pembukuan Online (Supabase)</p>
        </div>
        {notAuthorized && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4 text-center">
            Email Anda belum diizinkan mengakses aplikasi ini. Hubungi owner untuk didaftarkan.
          </div>
        )}
        <button onClick={onLogin} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
          </svg>
          Login dengan Gmail
        </button>
      </div>
    </div>
  );
}

// ========== SIDEBAR ==========
function Sidebar({ isOpen, setIsOpen, activePage, setActivePage, onLogout, userName }) {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'therapists', label: 'Gaji Staff & Terapis', icon: Users },
    { id: 'payroll', label: 'Rincian Komisi Harian', icon: DollarSign },
    { id: 'expenses', label: 'Expenses', icon: ShoppingCart },
    { id: 'financial-report', label: 'Laporan Keuangan', icon: Wallet },
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setIsOpen(false)}></div>}
      <div className={`fixed md:relative w-64 bg-gradient-to-b from-sky-500 to-blue-700 text-white h-screen overflow-y-auto transform transition-transform duration-300 z-50 ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-4 border-b border-blue-400">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">ATLAS Men's Health</h1>
            <button onClick={() => setIsOpen(false)} className="md:hidden"><X size={20} /></button>
          </div>
          <p className="text-blue-100 text-sm mt-1 truncate">{userName}</p>
        </div>
        <nav className="p-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { setActivePage(item.id); window.innerWidth < 768 && setIsOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activePage === item.id ? 'bg-white text-blue-600 font-semibold' : 'text-blue-50 hover:bg-blue-500'
                  }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-400">
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 transition text-white font-semibold">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}

function Header({ userName, onMenuClick }) {
  return (
    <div className="bg-white shadow">
      <div className="px-6 py-4 flex items-center justify-between">
        <button onClick={onMenuClick} className="md:hidden"><Menu size={24} /></button>
        <h2 className="text-2xl font-bold text-gray-800">Dashboard ATLAS</h2>
        <div className="text-sm text-gray-600">{new Date().toLocaleDateString('id-ID')}</div>
      </div>
    </div>
  );
}

// ========== DASHBOARD (Analytics) ==========
function Dashboard({ month: filterMonth, setMonth: setFilterMonth, year: filterYear, setYear: setFilterYear, startDate, setStartDate, endDate, setEndDate }) {
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netProfit, setNetProfit] = useState(0);

  const [paymentData, setPaymentData] = useState([]);
  const [topTherapists, setTopTherapists] = useState([]);
  const [therapistStatuses, setTherapistStatuses] = useState([]);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      // PENGGUNAAN SIKLUS KALENDER MURNI
      const { startLocal, endLocal } = getCalendarPeriodRange(filterMonth, filterYear, startDate, endDate);

      const periodStartStr = startLocal.toISOString();
      const periodEndStr = endLocal.toISOString();

      const { data: periodOrders } = await supabase
        .from('orders')
        .select('id, total_amount, payment_method, paid_at, order_items(item_kind, quantity, subtotal, therapist_id, therapists(name), menu_item_id, menu_items(name, category))')
        .eq('status', 'paid')
        .gte('paid_at', periodStartStr)
        .lte('paid_at', periodEndStr);

      const list = periodOrders || [];

      // ==========================================
      // INTEGRASI HISTORICAL DATA DINAMIS
      // ==========================================
      const dailyDataArray = [];
      let curr = new Date(startLocal);
      curr.setHours(0,0,0,0);
      const endD = new Date(endLocal);
      endD.setHours(0,0,0,0);

      let hcRevenue = 0, hcPax = 0, hcCash = 0, hcTransfer = 0;

      while(curr <= endD) {
        const y = curr.getFullYear();
        const m = String(curr.getMonth() + 1).padStart(2, '0');
        const dStr = String(curr.getDate()).padStart(2, '0');
        const dateKey = `${y}-${m}-${dStr}`;
        
        let dailyRev = 0;
        if (HARDCODE_DAILY[dateKey]) {
            dailyRev = HARDCODE_DAILY[dateKey].revenue;
            hcRevenue += HARDCODE_DAILY[dateKey].revenue;
            hcPax += HARDCODE_DAILY[dateKey].pax;
            hcCash += HARDCODE_DAILY[dateKey].cash;
            hcTransfer += HARDCODE_DAILY[dateKey].transfer;
        }

        dailyDataArray.push({ 
            day: `${dStr}/${m}`, 
            revenue: dailyRev, 
            _date: new Date(curr) 
        });
        curr.setDate(curr.getDate() + 1);
      }

      let dbRevenue = 0;
      let dbTransactions = 0;

      list.forEach(o => {
        if (o.paid_at) {
          const dObj = new Date(o.paid_at);
          const dateKey = `${dObj.getFullYear()}-${String(dObj.getMonth() + 1).padStart(2, '0')}-${String(dObj.getDate()).padStart(2, '0')}`;
          
          if (!HARDCODE_DAILY[dateKey]) {
            dbRevenue += Number(o.total_amount);
            dbTransactions += 1;
          }
        }
      });

      const finalRevenue = dbRevenue + hcRevenue;
      const finalTransactions = dbTransactions + hcPax;

      setTotalRevenue(finalRevenue);
      setTotalTransactions(finalTransactions);

      // Komisi GRO & Terapis memakai siklus payroll 22-21 (sama dengan Rekap Gaji & Laporan Keuangan)
      const { startLocal: commStart, endLocal: commEnd } = getPayrollPeriodRange(filterMonth, filterYear, startDate, endDate);

      const [
        { data: therapists }, 
        { data: commissions }, 
        { data: expensesData },
        { data: lemburData }
      ] = await Promise.all([
        supabase.from('therapists').select('*'), 
        supabase.from('payroll_commissions').select('staff_name, staff_role, commission_amount').gte('created_at', commStart.toISOString()).lte('created_at', commEnd.toISOString()),
        supabase.from('expenses').select('amount, category').gte('created_at', periodStartStr).lte('created_at', periodEndStr),
        supabase.from('lembur_logs').select('uang_lembur').gte('tanggal', toLocalDateStr(startLocal)).lte('tanggal', toLocalDateStr(endLocal))
      ]);

      let totalPengeluaranExpenses = 0;
      (expensesData || []).forEach(e => {
        totalPengeluaranExpenses += Number(e.amount || 0);
      });

      let totalLembur = 0;
      (lemburData || []).forEach(l => {
        totalLembur += Number(l.uang_lembur || 0);
      });

      const komisiTerapisMap = {};
      let totalKomisiGro = 0;
      (commissions || []).forEach(c => {
        const role = (c.staff_role || '').toLowerCase();
        const staffKey = (c.staff_name || '').trim().toLowerCase();
        if (role.includes('gro')) {
          totalKomisiGro += Number(c.commission_amount || 0);
        } else {
          komisiTerapisMap[staffKey] = (komisiTerapisMap[staffKey] || 0) + Number(c.commission_amount || 0);
        }
      });

      // Komisi terapis dihitung KOTOR (retensi 20% hanya ditahan, bukan pengurang biaya)
      let totalKomisiTerapis = 0;
      (therapists || []).filter(t => t.is_active).forEach(t => {
        const tKey = (t.name || '').trim().toLowerCase();
        totalKomisiTerapis += komisiTerapisMap[tKey] || 0;
      });

      const finalExpenses = totalPengeluaranExpenses + totalLembur + totalKomisiGro + totalKomisiTerapis;

      setTotalExpenses(finalExpenses);
      setNetProfit(finalRevenue - finalExpenses);

      const payMap = {};
      list.forEach(o => {
        if (o.paid_at) {
          const dObj = new Date(o.paid_at);
          const dateKey = `${dObj.getFullYear()}-${String(dObj.getMonth() + 1).padStart(2, '0')}-${String(dObj.getDate()).padStart(2, '0')}`;
          
          if (!HARDCODE_DAILY[dateKey]) {
            const key = o.payment_method || 'lainnya';
            payMap[key] = (payMap[key] || 0) + Number(o.total_amount);
          }
        }
      });
      
      payMap['cash'] = (payMap['cash'] || 0) + hcCash;
      payMap['transfer'] = (payMap['transfer'] || 0) + hcTransfer;
      
      setPaymentData(Object.entries(payMap).map(([name, value]) => ({ name, value })));

      // Terapis Terbaik memakai siklus payroll 22-21 (sama dengan Bonus Tamu di Rekap Gaji)
      const { data: tamuOrders } = await supabase
        .from('orders')
        .select('id, paid_at, order_items(item_kind, therapist_id, subtotal, therapists(name))')
        .eq('status', 'paid')
        .gte('paid_at', commStart.toISOString())
        .lte('paid_at', commEnd.toISOString());

      const therapistMap = {};
      (tamuOrders || []).forEach(o => {
        if (o.paid_at) {
          const dObj = new Date(o.paid_at);
          const dateKey = `${dObj.getFullYear()}-${String(dObj.getMonth() + 1).padStart(2, '0')}-${String(dObj.getDate()).padStart(2, '0')}`;
          
          if (!HARDCODE_DAILY[dateKey]) {
            (o.order_items || []).forEach(item => {
              if (item.item_kind === 'treatment' && item.therapist_id) {
                const name = item.therapists?.name || 'Unknown';
                if (!therapistMap[name]) {
                  therapistMap[name] = { count: 0, revenue: 0, _orderIds: new Set() };
                }
                if (!therapistMap[name]._orderIds.has(o.id)) {
                  therapistMap[name]._orderIds.add(o.id);
                  therapistMap[name].count += 1;
                }
                therapistMap[name].revenue += Number(item.subtotal || 0);
              }
            });
          }
        }
      });

      // Urutan: jumlah tamu -> revenue -> total komisi -> abjad
      const rankedTherapists = Object.entries(therapistMap)
        .map(([name, data]) => ({
          name,
          count: data.count,
          revenue: data.revenue,
          komisi: komisiTerapisMap[(name || '').trim().toLowerCase()] || 0
        }))
        .sort((a, b) =>
          b.count - a.count ||
          b.revenue - a.revenue ||
          b.komisi - a.komisi ||
          a.name.localeCompare(b.name)
        )
        .slice(0, 10);

      setTopTherapists(rankedTherapists);

      const { data: allTherapists } = await supabase
        .from('therapists')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      const { data: ongoingOrders } = await supabase
        .from('orders')
        .select('order_items(therapist_id)')
        .neq('status', 'paid');

      const busyIds = new Set();
      (ongoingOrders || []).forEach(o => {
        (o.order_items || []).forEach(item => {
          if (item.therapist_id) busyIds.add(item.therapist_id);
        });
      });

      const statusList = (allTherapists || []).map(t => ({
        id: t.id,
        name: t.name,
        isBusy: busyIds.has(t.id)
      }));
      setTherapistStatuses(statusList);

      list.forEach(o => {
        if (o.paid_at) {
          const localDate = new Date(o.paid_at); 
          const dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
          
          if (!HARDCODE_DAILY[dateKey]) {
            localDate.setHours(0,0,0,0);
            const target = dailyDataArray.find(d => d._date.getTime() === localDate.getTime());
            if (target) {
              target.revenue += Number(o.total_amount) || 0;
            }
          }
        }
      });
      setDailyRevenue(dailyDataArray);

      const yearStart = `${filterYear}-01-01T00:00:00`;
      const yearEnd = `${filterYear}-12-31T23:59:59`;

      const { data: yearlyOrders } = await supabase
        .from('orders')
        .select('total_amount, paid_at')
        .eq('status', 'paid')
        .gte('paid_at', yearStart)
        .lte('paid_at', yearEnd);

      const monthsData = Array.from({ length: 12 }, (_, i) => ({
        month: MONTH_NAMES[i].slice(0, 3),
        revenue: 0,
      }));
      
      Object.entries(HARDCODE_DAILY).forEach(([dateStr, data]) => {
        const [yStr, mStr] = dateStr.split('-');
        if (parseInt(yStr, 10) === filterYear) {
          const monthIndex = parseInt(mStr, 10) - 1;
          monthsData[monthIndex].revenue += data.revenue;
        }
      });

      (yearlyOrders || []).forEach(o => {
        if (o.paid_at) {
            const localDate = new Date(o.paid_at);
            if(localDate.getFullYear() === filterYear) {
                const dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
                
                if (!HARDCODE_DAILY[dateKey]) {
                    const m = localDate.getMonth();
                    monthsData[m].revenue += Number(o.total_amount);
                }
            }
        }
      });
      setMonthlyData(monthsData);

      setLoading(false);
    };

    fetchDashboardData();

    const dashboardSub = supabase.channel('dashboard-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => { fetchDashboardData(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'expenses' }, () => { fetchDashboardData(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => { fetchDashboardData(); })
      .subscribe();

    return () => supabase.removeChannel(dashboardSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear, startDate, endDate]);

  const displayLabel = getCalendarDisplayLabel(startDate, endDate, filterMonth, filterYear);

  return (
    <div className="space-y-6">
      <FilterBar 
        month={filterMonth} setMonth={setFilterMonth} 
        year={filterYear} setYear={setFilterYear} 
        startDate={startDate} setStartDate={setStartDate} 
        endDate={endDate} setEndDate={setEndDate} 
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-900 text-white rounded-lg shadow p-5 flex flex-col justify-center min-h-[120px]">
          <p className="text-gray-300 text-xs font-medium mb-1 flex justify-between items-center">
            Total Pendapatan
            <span className="bg-gray-800 px-2 py-0.5 rounded text-[10px] whitespace-nowrap">{displayLabel}</span>
          </p>
          {loading ? <div className="h-8 animate-pulse bg-gray-700 rounded w-1/2 mt-2"></div> : (
            <p className="text-3xl font-bold mt-1">Rp {totalRevenue.toLocaleString('id-ID')}</p>
          )}
        </div>

        <div className="bg-white border rounded-lg shadow p-5 flex flex-col justify-center min-h-[120px]">
          <p className="text-gray-500 text-xs font-medium mb-1 flex justify-between items-center">
            <span className="flex items-center gap-1"><ReceiptIcon size={14} /> Total Transaksi</span>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] whitespace-nowrap">{displayLabel}</span>
          </p>
          {loading ? <div className="h-8 animate-pulse bg-gray-200 rounded w-1/3 mt-2"></div> : (
            <p className="text-3xl font-bold text-gray-900 mt-1">{totalTransactions.toLocaleString('id-ID')}</p>
          )}
        </div>

        <div className="bg-white border rounded-lg shadow p-5 flex flex-col justify-center min-h-[120px]">
          <p className="text-gray-500 text-xs font-medium mb-1 flex justify-between items-center">
            <span className="flex items-center gap-1"><ShoppingCart size={14} /> Total Pengeluaran</span>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] whitespace-nowrap">{displayLabel}</span>
          </p>
          {loading ? <div className="h-8 animate-pulse bg-gray-200 rounded w-1/3 mt-2"></div> : (
            <p className="text-3xl font-bold text-red-600 mt-1">Rp {totalExpenses.toLocaleString('id-ID')}</p>
          )}
        </div>

        <div className="bg-white border rounded-lg shadow p-5 flex flex-col justify-center min-h-[120px]">
          <p className="text-gray-500 text-xs font-medium mb-1 flex justify-between items-center">
            <span className="flex items-center gap-1"><TrendingUp size={14} /> Profit Bersih</span>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] whitespace-nowrap">{displayLabel}</span>
          </p>
          {loading ? <div className="h-8 animate-pulse bg-gray-200 rounded w-1/3 mt-2"></div> : (
            <p className={`text-3xl font-bold mt-1 ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Rp {netProfit.toLocaleString('id-ID')}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Memuat visualisasi grafik...</div>
      ) : (
        <>
          {/* Status Terapis */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-bold mb-4 text-gray-700">Status Terapis Saat Ini</h3>
            {therapistStatuses.length === 0 ? (
              <p className="text-xs text-gray-400">Belum ada data terapis.</p>
            ) : (
              <div className="w-full">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {therapistStatuses.map(t => (
                    <div
                      key={t.id}
                      className={`flex items-center justify-center h-12 rounded-md border text-sm font-bold shadow-sm transition-colors cursor-default px-1 text-center ${t.isBusy
                        ? 'bg-red-500 border-red-600 text-white'
                        : 'bg-green-500 border-green-600 text-white'
                        }`}
                      title={`${t.name} - ${t.isBusy ? 'Sedang Bekerja' : 'Tersedia'}`}
                    >
                      <span className="truncate w-full">{t.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-bold mb-4 text-gray-700">Revenue per Bulan (Tahun {filterYear})</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip cursor={false} formatter={(v) => `Rp ${Number(v).toLocaleString('id-ID')}`} />
                  <Bar dataKey="revenue" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-bold mb-4 text-gray-700">Cara Pembayaran ({displayLabel})</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={paymentData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" label={({ name }) => name}>
                    {paymentData.map((entry, i) => <Cell key={i} fill={PAYMENT_COLORS[entry.name] || '#9ca3af'} />)}
                  </Pie>
                  <Tooltip cursor={false} formatter={(v) => `Rp ${Number(v).toLocaleString('id-ID')}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Award size={16} className="text-amber-500" /> Terapis Terbaik ({getPayrollDisplayLabel(startDate, endDate, filterMonth, filterYear)})
              </h3>
              <span className="text-[11px] text-gray-400 font-medium">Berdasarkan Tamu</span>
            </div>

            {topTherapists.length === 0 ? (
              <p className="text-xs text-gray-400">Belum ada data untuk periode ini</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, topTherapists.length * 32)}>
                <BarChart data={topTherapists} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 border rounded-lg shadow-lg text-xs space-y-1">
                            <p className="font-bold text-gray-800 border-b pb-1">{data.name}</p>
                            <p className="text-gray-600">
                              Jumlah Tamu: <span className="font-semibold text-teal-600">{data.count} Tamu</span>
                            </p>
                            <p className="text-gray-600">
                              Total Revenue: <span className="font-semibold text-purple-700">Rp {Number(data.revenue).toLocaleString('id-ID')}</span>
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" fill="#14b8a6" radius={[0, 4, 4, 0]} barSize={16}>
                    <LabelList dataKey="count" position="right" formatter={(v) => `${v} Tamu`} style={{ fontSize: 11, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-bold mb-4 text-gray-700">Tren Revenue per Hari ({displayLabel})</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={dailyRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={15} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip cursor={false} formatter={(v) => `Rp ${Number(v).toLocaleString('id-ID')}`} />
                <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ========== THERAPISTS PAGE (GAJI STAFF & TERAPIS) ==========
function TherapistsPage({ month, setMonth, year, setYear, startDate, setStartDate, endDate, setEndDate }) {
  const [activeTab, setActiveTab] = useState('rekap-gaji');

  return (
    <div className="space-y-4">
      {activeTab === 'rekap-gaji' && (
        <FilterBar 
          month={month} setMonth={setMonth} 
          year={year} setYear={setYear} 
          startDate={startDate} setStartDate={setStartDate} 
          endDate={endDate} setEndDate={setEndDate} 
        />
      )}

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-max">
        <button
          onClick={() => setActiveTab('rekap-gaji')}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${activeTab === 'rekap-gaji' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Rekap Penggajian
        </button>
        <button
          onClick={() => setActiveTab('retensi')}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${activeTab === 'retensi' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Potongan 20% Terapis
        </button>
      </div>

      {activeTab === 'rekap-gaji' && <GajiKomisiTerapisTab month={month} year={year} startDate={startDate} endDate={endDate} />}
      {activeTab === 'retensi' && <RetensiDanaTab />}
    </div>
  );
}

function GajiKomisiTerapisTab({ month, year: yearFilter, startDate, endDate }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({ THERAPIST: [], 'GRO & KASIR': [], ADMIN: [], HOUSEKEEPING: [], SECURITY: [], LAINNYA: [] });
  const [grandTotals, setGrandTotals] = useState({ pokok: 0, komisi: 0, lembur: 0, totalGajiKomisi: 0, tabungan: 0, piutang: 0, dibayarkan: 0 });

  const [bonusTamuRows, setBonusTamuRows] = useState([]);
  const [totalBonusTamu, setTotalBonusTamu] = useState(0);

  const [supplierRows, setSupplierRows] = useState([]);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ supplier_name: '', referred_name: '', join_date: new Date().toISOString().split('T')[0], commission_amount: 1500000, note: '' });
  const [supplierSubmitting, setSupplierSubmitting] = useState(false);
  const [supplierError, setSupplierError] = useState('');
  const [periodRange, setPeriodRange] = useState({ startLocal: null, endLocal: null });
  const [exporting, setExporting] = useState(false);

  const groupByRole = (role) => {
    const r = (role || '').toLowerCase();
    if (r.includes('gro') || r.includes('kasir')) return 'GRO & KASIR';
    if (r.includes('admin')) return 'ADMIN';
    if (r.includes('house')) return 'HOUSEKEEPING';
    if (r.includes('security')) return 'SECURITY';
    return 'LAINNYA';
  };

  const fetchData = async () => {
    setLoading(true);

    // PENGGUNAAN SIKLUS PAYROLL (22 s/d 21)
    const { startLocal, endLocal } = getPayrollPeriodRange(month, yearFilter, startDate, endDate);
    const { startStr: startMonthStr, endStr: endMonthStr } = toLocalRangeStrings(startLocal, endLocal);
    const periodStartISO = startLocal.toISOString();
    const periodEndISO = endLocal.toISOString();
    setPeriodRange({ startLocal, endLocal });

    const [staffRes, lemburRes, commRes, therRes, ordersRes, supplierRes] = await Promise.all([
      supabase.from('staff').select('*').eq('is_active', true).order('created_at'),
      supabase.from('lembur_logs').select('*').gte('tanggal', startMonthStr).lte('tanggal', endMonthStr),
      supabase.from('payroll_commissions').select('staff_name, staff_role, commission_amount').gte('created_at', startMonthStr).lte('created_at', endMonthStr),
      supabase.from('therapists').select('*').eq('is_active', true).order('name'),
      supabase.from('orders').select('id, paid_at, order_items(item_kind, therapist_id, subtotal, therapists(name))').eq('status', 'paid').gte('paid_at', periodStartISO).lte('paid_at', periodEndISO),
      supabase.from('therapist_referral_commissions').select('*').order('join_date', { ascending: false })
    ]);

    const staffList = staffRes.data || [];
    const lemburLogs = lemburRes.data || [];
    const commissions = commRes.data || [];
    const therapists = therRes.data || [];
    const orders = ordersRes.data || [];
    if (ordersRes.error) {
      console.error('[GajiKomisiTerapisTab] Gagal mengambil data orders untuk Bonus Tamu:', ordersRes.error);
    }

    // ===== LEMBUR PER NAMA =====
    const lemburByStaffName = {};
    lemburLogs.forEach(l => {
      const key = (l.nama_pegawai || '').trim().toLowerCase();
      lemburByStaffName[key] = (lemburByStaffName[key] || 0) + Number(l.uang_lembur || 0);
    });

    // ===== KOMISI PER NAMA (GRO vs TERAPIS) =====
    const komisiGroMap = {};
    const komisiTerapisMap = {};
    commissions.forEach(c => {
      const role = (c.staff_role || '').toLowerCase();
      const staffKey = (c.staff_name || '').trim().toLowerCase();
      if (role.includes('gro')) {
        komisiGroMap[staffKey] = (komisiGroMap[staffKey] || 0) + Number(c.commission_amount || 0);
      } else {
        komisiTerapisMap[staffKey] = (komisiTerapisMap[staffKey] || 0) + Number(c.commission_amount || 0);
      }
    });

    // Catatan: Piutang (kasbon) per orang belum tersambung -- menunggu skema
    // tabel kasbon_transactions. Sementara ditetapkan 0 untuk semua baris.
    const piutangMap = {};

    const buildRow = (name, role, pokok, komisi, lembur, tabungan) => {
      const piutang = piutangMap[(name || '').trim().toLowerCase()] || 0;
      const totalGajiKomisi = pokok + komisi;
      const gajiDiterima = totalGajiKomisi + lembur - tabungan - piutang;
      return { name, role, pokok, komisi, lembur, totalGajiKomisi, tabungan, piutang, gajiDiterima, keterangan: '-' };
    };

    // ===== BONUS TAMU: ranking sama seperti "Terapis Terbaik" di Dashboard =====
    // (jumlah order unik per terapis dalam periode ini, tanpa hari HARDCODE_DAILY)
    // Dihitung di sini (sebelum baris terapis dibuat) karena bonus ini menggantikan
    // kolom Lembur pada tabel Therapist dan ikut masuk ke Gaji Diterima per terapis.
    const tamuMap = {};
    orders.forEach(o => {
      if (!o.paid_at) return;
      const dObj = new Date(o.paid_at);
      const dateKey = `${dObj.getFullYear()}-${String(dObj.getMonth() + 1).padStart(2, '0')}-${String(dObj.getDate()).padStart(2, '0')}`;
      if (HARDCODE_DAILY[dateKey]) return;
      const seenTherapistInOrder = new Set();
      (o.order_items || []).forEach(item => {
        if (item.item_kind === 'treatment' && item.therapist_id) {
          const name = item.therapists?.name || 'Unknown';
          if (!tamuMap[name]) tamuMap[name] = { count: 0, revenue: 0 };
          if (!seenTherapistInOrder.has(name)) {
            seenTherapistInOrder.add(name);
            tamuMap[name].count += 1;
          }
          tamuMap[name].revenue += Number(item.subtotal || 0);
        }
      });
    });

    // Urutan sama persis dengan Dashboard: jumlah tamu -> revenue -> total komisi -> abjad
    const rankedTamu = Object.entries(tamuMap)
      .map(([name, data]) => ({
        name,
        count: data.count,
        revenue: data.revenue,
        komisi: komisiTerapisMap[(name || '').trim().toLowerCase()] || 0
      }))
      .sort((a, b) =>
        b.count - a.count ||
        b.revenue - a.revenue ||
        b.komisi - a.komisi ||
        a.name.localeCompare(b.name)
      )
      .slice(0, 10)
      .map((row, idx) => {
        const rank = idx + 1;
        const bonus = rank <= 4 ? 1250000 : rank <= 7 ? 750000 : 250000;
        return { ...row, rank, bonus };
      });

    setBonusTamuRows(rankedTamu);
    setTotalBonusTamu(rankedTamu.reduce((sum, r) => sum + r.bonus, 0));

    const bonusTamuMap = {};
    rankedTamu.forEach(r => { bonusTamuMap[r.name] = r.bonus; });

    const newGroups = { THERAPIST: [], 'GRO & KASIR': [], ADMIN: [], HOUSEKEEPING: [], SECURITY: [], LAINNYA: [] };

    therapists.forEach(t => {
      const tKey = (t.name || '').trim().toLowerCase();
      const pokok = Number(t.salary || t.base_salary_monthly || 0);
      const komisi = komisiTerapisMap[tKey] || 0;
      const tabungan = komisi * 0.2; // potongan retensi 20% dari komisi
      const bonusTamu = bonusTamuMap[t.name] || 0; // menggantikan kolom Lembur utk Therapist
      newGroups.THERAPIST.push(buildRow(t.name, 'Terapis', pokok, komisi, bonusTamu, tabungan));
    });

    const handledStaffKeys = new Set();
    staffList.forEach(s => {
      const staffKey = (s.name || '').trim().toLowerCase();
      handledStaffKeys.add(staffKey);
      const pokok = Number(s.base_salary_monthly || s.salary || 0);
      const lembur = lemburByStaffName[staffKey] || 0;
      const isGro = (s.role || '').toLowerCase().includes('gro');
      const komisi = isGro ? (komisiGroMap[staffKey] || 0) : 0;
      const groupKey = groupByRole(s.role);
      newGroups[groupKey].push(buildRow(s.name, s.role, pokok, komisi, lembur, 0));
    });

    // Lembur milik nama yang tidak ketemu di tabel staff (jaga-jaga)
    Object.entries(lemburByStaffName).forEach(([key, lembur]) => {
      if (!handledStaffKeys.has(key)) {
        newGroups.LAINNYA.push(buildRow(key.toUpperCase(), null, 0, 0, lembur, 0));
      }
    });

    setGroups(newGroups);

    const allRows = Object.values(newGroups).flat();
    const totals = allRows.reduce((acc, r) => ({
      pokok: acc.pokok + r.pokok,
      komisi: acc.komisi + r.komisi,
      lembur: acc.lembur + r.lembur,
      totalGajiKomisi: acc.totalGajiKomisi + r.totalGajiKomisi,
      tabungan: acc.tabungan + r.tabungan,
      piutang: acc.piutang + r.piutang,
      dibayarkan: acc.dibayarkan + r.gajiDiterima,
    }), { pokok: 0, komisi: 0, lembur: 0, totalGajiKomisi: 0, tabungan: 0, piutang: 0, dibayarkan: 0 });
    setGrandTotals(totals);

    // ===== KOMISI SUPPLIER (referral terapis) =====
    const todayStr = new Date().toISOString().split('T')[0];
    const supplierComputed = (supplierRes.data || []).map(row => {
      const jatuhTempo = new Date(row.join_date);
      jatuhTempo.setMonth(jatuhTempo.getMonth() + 1);
      const jatuhTempoStr = jatuhTempo.toISOString().split('T')[0];
      let status = 'BELUM JATUH TEMPO';
      if (row.is_paid) status = 'LUNAS';
      else if (jatuhTempoStr <= todayStr) status = 'JATUH TEMPO';
      return { ...row, jatuhTempoStr, status };
    });
    setSupplierRows(supplierComputed);

    setLoading(false);
  };

  const fetchSupplierOnly = async () => {
    const { data } = await supabase.from('therapist_referral_commissions').select('*').order('join_date', { ascending: false });
    const todayStr = new Date().toISOString().split('T')[0];
    const supplierComputed = (data || []).map(row => {
      const jatuhTempo = new Date(row.join_date);
      jatuhTempo.setMonth(jatuhTempo.getMonth() + 1);
      const jatuhTempoStr = jatuhTempo.toISOString().split('T')[0];
      let status = 'BELUM JATUH TEMPO';
      if (row.is_paid) status = 'LUNAS';
      else if (jatuhTempoStr <= todayStr) status = 'JATUH TEMPO';
      return { ...row, jatuhTempoStr, status };
    });
    setSupplierRows(supplierComputed);
  };

  const handleSubmitSupplier = async () => {
    if (!supplierForm.supplier_name || !supplierForm.referred_name) return setSupplierError('Nama supplier dan terapis wajib diisi');
    if (!supplierForm.join_date) return setSupplierError('Tanggal masuk wajib diisi');
    setSupplierSubmitting(true); setSupplierError('');
    const { error } = await supabase.from('therapist_referral_commissions').insert({
      supplier_name: supplierForm.supplier_name,
      referred_name: supplierForm.referred_name,
      join_date: supplierForm.join_date,
      commission_amount: Number(supplierForm.commission_amount) || 0,
      note: supplierForm.note || null,
    });
    setSupplierSubmitting(false);
    if (error) return setSupplierError('Gagal menyimpan: ' + error.message);
    setSupplierForm({ supplier_name: '', referred_name: '', join_date: new Date().toISOString().split('T')[0], commission_amount: 1500000, note: '' });
    setShowSupplierForm(false);
    fetchSupplierOnly();
  };

  const handleTogglePaidSupplier = async (row) => {
    await supabase.from('therapist_referral_commissions').update({ is_paid: !row.is_paid, paid_at: !row.is_paid ? new Date().toISOString() : null }).eq('id', row.id);
    fetchSupplierOnly();
  };


  // ===== EXPORT KE EXCEL (mengikuti format file referensi: Lembur apa adanya,
  // Bonus Tamu & Komisi Supplier sebagai kontrol terpisah, tidak digabung ke
  // Gaji Diterima -- beda dari tampilan layar yang sudah menggabung Bonus Tamu
  // ke kolom Lembur Therapist) =====
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const fmtDate = (d) => d ? `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}` : '-';

      // Pakai data persis seperti di layar: Bonus Tamu sudah tergabung ke kolom
      // Lembur pada grup THERAPIST dan sudah menambah Gaji Diterima masing-masing.
      const exportGroups = groups;
      const totalsExport = grandTotals;

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('PAYROLL ALL DIVISI');
      ws.columns = [
        { width: 6 }, { width: 18.35 }, { width: 16 }, { width: 16 }, { width: 14 },
        { width: 20 }, { width: 14 }, { width: 14 }, { width: 18 }, { width: 24 },
      ];

      const DARK = 'FF5B6770', GRAY = 'FF7F8C8D', GREEN = 'FFE2F0D9', SLATE = 'FF374151', BLUE = 'FF1F4E78', LBLUE = 'FFD9EAF7';
      const fillCell = (cell, color, white) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        cell.font = { bold: true, color: { argb: white ? 'FFFFFFFF' : 'FF000000' } };
      };
      const num = (cell, v) => { cell.value = v; cell.numFmt = '#,##0'; };

      let r = 1;
      ws.mergeCells(r, 1, r, 10);
      ws.getCell(r, 1).value = 'PAYROLL ATLAS';
      ws.getCell(r, 1).font = { bold: true, size: 14 };
      r++;
      ws.mergeCells(r, 1, r, 10);
      ws.getCell(r, 1).value = `Periode: ${displayLabel}`;
      ws.getCell(r, 1).font = { italic: true, color: { argb: 'FF666666' } };
      r += 2;

      const writeDivisi = (title, rows) => {
        ws.mergeCells(r, 1, r, 10);
        fillCell(ws.getCell(r, 1), DARK, true);
        ws.getCell(r, 1).value = title;
        r++;
        const headers = ['No', 'Nama', 'Gaji Pokok', 'Komisi', 'Tabungan 20%', 'Total Gaji + Komisi', title === 'THERAPIST' ? 'Bonus Tamu' : 'Lembur', 'Piutang', 'Gaji Diterima', 'Keterangan'];
        headers.forEach((h, i) => { fillCell(ws.getCell(r, i + 1), GRAY, true); ws.getCell(r, i + 1).value = h; });
        r++;
        rows.forEach((row, idx) => {
          ws.getCell(r, 1).value = idx + 1;
          ws.getCell(r, 2).value = row.name;
          num(ws.getCell(r, 3), row.pokok);
          num(ws.getCell(r, 4), row.komisi);
          num(ws.getCell(r, 5), row.tabungan);
          num(ws.getCell(r, 6), row.totalGajiKomisi);
          num(ws.getCell(r, 7), row.lembur);
          num(ws.getCell(r, 8), row.piutang);
          num(ws.getCell(r, 9), row.gajiDiterima);
          ws.getCell(r, 10).value = row.keterangan === '-' ? '' : row.keterangan;
          r++;
        });
        const sub = rows.reduce((a, x) => ({
          pokok: a.pokok + x.pokok, komisi: a.komisi + x.komisi, lembur: a.lembur + x.lembur,
          totalGajiKomisi: a.totalGajiKomisi + x.totalGajiKomisi, tabungan: a.tabungan + x.tabungan,
          piutang: a.piutang + x.piutang, gajiDiterima: a.gajiDiterima + x.gajiDiterima,
        }), { pokok: 0, komisi: 0, lembur: 0, totalGajiKomisi: 0, tabungan: 0, piutang: 0, gajiDiterima: 0 });
        ws.getCell(r, 2).value = `TOTAL ${title}`;
        num(ws.getCell(r, 3), sub.pokok); num(ws.getCell(r, 4), sub.komisi); num(ws.getCell(r, 5), sub.tabungan);
        num(ws.getCell(r, 6), sub.totalGajiKomisi); num(ws.getCell(r, 7), sub.lembur); num(ws.getCell(r, 8), sub.piutang);
        num(ws.getCell(r, 9), sub.gajiDiterima);
        for (let c = 1; c <= 10; c++) { fillCell(ws.getCell(r, c), GREEN, false); }
        r += 2;
      };

      [['THERAPIST', exportGroups.THERAPIST], ['GRO & KASIR', exportGroups['GRO & KASIR']], ['ADMIN', exportGroups.ADMIN],
       ['HOUSEKEEPING', exportGroups.HOUSEKEEPING], ['SECURITY', exportGroups.SECURITY], ['LAINNYA', exportGroups.LAINNYA]]
        .filter(([, rows]) => rows.length > 0)
        .forEach(([title, rows]) => writeDivisi(title, rows));

      // ===== TOTAL PENGELUARAN GAJI - SEMUA DIVISI =====
      ws.mergeCells(r, 1, r, 10);
      fillCell(ws.getCell(r, 1), SLATE, true);
      ws.getCell(r, 1).value = 'TOTAL PENGELUARAN GAJI — SEMUA DIVISI';
      r++;
      const totalBeban = totalsExport.totalGajiKomisi + totalsExport.lembur;
      const ringkasanRows = [
        ['Total Gaji Pokok', totalsExport.pokok],
        ['Total Komisi', totalsExport.komisi],
        ['Total Lembur + Bonus Tamu', totalsExport.lembur],
        ['Total Gaji + Komisi', totalsExport.totalGajiKomisi],
        ['Total Tabungan 20% (Therapist saja)', totalsExport.tabungan],
        ['Total Piutang', totalsExport.piutang],
      ];
      ringkasanRows.forEach(([label, val]) => { ws.getCell(r, 2).value = label; num(ws.getCell(r, 9), val); r++; });
      ws.getCell(r, 2).value = 'TOTAL DIBAYARKAN';
      num(ws.getCell(r, 9), totalsExport.dibayarkan);
      for (let c = 1; c <= 10; c++) { fillCell(ws.getCell(r, c), GREEN, false); }
      r += 2;

      // ===== TABEL CONTROL & CHECK/RECHECK =====
      ws.mergeCells(r, 1, r, 6);
      fillCell(ws.getCell(r, 1), BLUE, true);
      ws.getCell(r, 1).value = 'TABEL CONTROL & CHECK / RECHECK';
      r += 2;
      ['No', 'ITEM CONTROL', 'NILAI', 'STATUS CHECK', 'CATATAN'].forEach((h, i) => { fillCell(ws.getCell(r, i + 1), LBLUE, false); ws.getCell(r, i + 1).value = h; });
      r++;
      const controlItems = [
        ['Total Gaji Pokok', totalsExport.pokok, 'OK', 'Sesuai total payroll'],
        ['Total Komisi', totalsExport.komisi, 'OK', 'Sesuai total payroll'],
        ['Total Lembur + Bonus Tamu', totalsExport.lembur, 'OK', 'Sudah termasuk Bonus Tamu Therapist'],
        ['Total Beban Payroll', totalBeban, 'OK', 'Gaji + Komisi + Lembur + Bonus Tamu'],
        ['Tabungan Therapist 20%', totalsExport.tabungan, 'CONTROL', 'Dipisahkan dari pembayaran therapist'],
        ['Total Piutang', totalsExport.piutang, 'CONTROL', 'Mengurangi uang yang dibayarkan'],
        ['Total Payroll Dibayarkan', totalsExport.dibayarkan, 'OK', 'Setelah tabungan & piutang'],
      ];
      controlItems.forEach(([label, val, status, note], idx) => {
        ws.getCell(r, 1).value = idx + 1;
        ws.getCell(r, 2).value = label;
        num(ws.getCell(r, 3), val);
        ws.getCell(r, 4).value = status;
        ws.getCell(r, 5).value = note;
        r++;
      });
      r++;

      // ===== CONTROL BONUS TAMU =====
      ws.mergeCells(r, 1, r, 6);
      fillCell(ws.getCell(r, 1), BLUE, true);
      ws.getCell(r, 1).value = 'CONTROL BONUS TAMU';
      r++;
      ws.mergeCells(r, 1, r, 6);
      ws.getCell(r, 1).value = 'Referensi saja -- nilainya sudah termasuk di kolom Bonus Tamu pada tabel THERAPIST, tidak dijumlah lagi di bawah.';
      ws.getCell(r, 1).font = { italic: true, size: 9, color: { argb: 'FF666666' } };
      r++;
      ['No', 'Nama', 'Total Tamu', 'Bonus', 'Status Check', 'Catatan'].forEach((h, i) => { fillCell(ws.getCell(r, i + 1), LBLUE, false); ws.getCell(r, i + 1).value = h; });
      r++;
      bonusTamuRows.forEach((row, idx) => {
        ws.getCell(r, 1).value = idx + 1;
        ws.getCell(r, 2).value = row.name;
        ws.getCell(r, 3).value = row.count;
        num(ws.getCell(r, 4), row.bonus);
        ws.getCell(r, 5).value = 'CHECK';
        ws.getCell(r, 6).value = 'Sesuai rekap bonus';
        r++;
      });
      ws.getCell(r, 2).value = 'TOTAL BONUS TAMU';
      num(ws.getCell(r, 4), totalBonusTamu);
      r += 2;

      // ===== CONTROL KOMISI SUPPLIER =====
      ws.mergeCells(r, 1, r, 8);
      fillCell(ws.getCell(r, 1), BLUE, true);
      ws.getCell(r, 1).value = 'CONTROL KOMISI SUPPLIER';
      r += 2;
      ['No', 'Supplier', 'Therapist', 'Tanggal Masuk', 'Komisi', 'Jatuh Tempo', 'Status', 'Catatan'].forEach((h, i) => { fillCell(ws.getCell(r, i + 1), LBLUE, false); ws.getCell(r, i + 1).value = h; });
      r++;
      supplierRows.forEach((row, idx) => {
        ws.getCell(r, 1).value = idx + 1;
        ws.getCell(r, 2).value = row.supplier_name;
        ws.getCell(r, 3).value = row.referred_name;
        ws.getCell(r, 4).value = fmtDate(new Date(row.join_date));
        num(ws.getCell(r, 5), row.commission_amount);
        ws.getCell(r, 6).value = fmtDate(new Date(row.jatuhTempoStr));
        ws.getCell(r, 7).value = row.status;
        ws.getCell(r, 8).value = row.note || 'Bayar setelah 1 bulan bekerja';
        r++;
      });
      const supplierAllTotalExport = supplierRows.reduce((s, x) => s + Number(x.commission_amount || 0), 0);
      const supplierJatuhTempoExport = supplierRows.filter(x => x.status === 'JATUH TEMPO').reduce((s, x) => s + Number(x.commission_amount || 0), 0);
      ws.getCell(r, 2).value = 'TOTAL KOMISI SUPPLIER';
      num(ws.getCell(r, 5), supplierAllTotalExport);
      r++;
      ws.getCell(r, 2).value = 'Tanggal Check / Cut-off';
      ws.getCell(r, 3).value = fmtDate(periodRange.endLocal);
      r += 2;

      // ===== HASIL CHECK / RECHECK =====
      ws.mergeCells(r, 1, r, 6);
      fillCell(ws.getCell(r, 1), BLUE, true);
      ws.getCell(r, 1).value = 'HASIL CHECK / RECHECK';
      r++;
      // Bonus Tamu sudah termasuk di dalam Payroll Dibayarkan (lihat Gaji Diterima
      // tabel THERAPIST), jadi tidak dijumlah lagi di sini supaya tidak dobel hitung.
      const totalUangKeluarExport = totalsExport.dibayarkan + supplierJatuhTempoExport;
      [
        ['Payroll Dibayarkan (termasuk Bonus Tamu)', totalsExport.dibayarkan],
        ['- di antaranya Bonus Tamu', totalBonusTamu],
        ['Supplier Sudah Jatuh Tempo', supplierJatuhTempoExport],
      ].forEach(([label, val]) => { ws.getCell(r, 1).value = label; num(ws.getCell(r, 2), val); r++; });
      ws.getCell(r, 1).value = 'TOTAL UANG KELUAR';
      num(ws.getCell(r, 2), totalUangKeluarExport);
      fillCell(ws.getCell(r, 1), GREEN, false);
      fillCell(ws.getCell(r, 2), GREEN, false);

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payroll_ATLAS_${displayLabel.replace(/[^0-9A-Za-z]+/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[GajiKomisiTerapisTab] Gagal export Excel:', err);
      alert('Gagal membuat file Excel: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
    const sub = supabase.channel('gaji-komisi-terapis-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'therapists' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lembur_logs' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => fetchData())
      .subscribe();
    return () => supabase.removeChannel(sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, yearFilter, startDate, endDate]);

  const displayLabel = getPayrollDisplayLabel(startDate, endDate, month, yearFilter);
  const rp = (n) => `Rp ${Math.round(n || 0).toLocaleString('id-ID')}`;

  const supplierJatuhTempoTotal = supplierRows.filter(r => r.status === 'JATUH TEMPO').reduce((s, r) => s + Number(r.commission_amount || 0), 0);
  const supplierAllTotal = supplierRows.reduce((s, r) => s + Number(r.commission_amount || 0), 0);
  // Bonus Tamu sudah termasuk di dalam Gaji Diterima tabel Therapist (grandTotals.dibayarkan),
  // jadi di sini tidak dijumlah lagi supaya tidak dobel hitung.
  const totalUangKeluar = grandTotals.dibayarkan + supplierJatuhTempoTotal;

  const DivisiTable = ({ title, rows, colorClass, colLabel4 = 'Lembur' }) => {
    if (rows.length === 0) return null;
    const sub = rows.reduce((acc, r) => ({
      pokok: acc.pokok + r.pokok, komisi: acc.komisi + r.komisi, lembur: acc.lembur + r.lembur,
      totalGajiKomisi: acc.totalGajiKomisi + r.totalGajiKomisi, tabungan: acc.tabungan + r.tabungan,
      piutang: acc.piutang + r.piutang, gajiDiterima: acc.gajiDiterima + r.gajiDiterima,
    }), { pokok: 0, komisi: 0, lembur: 0, totalGajiKomisi: 0, tabungan: 0, piutang: 0, gajiDiterima: 0 });

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
        <div className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest ${colorClass}`}>{title}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">No</th>
                <th className="px-3 py-2 text-left">Nama</th>
                <th className="px-3 py-2 text-right">Gaji Pokok</th>
                <th className="px-3 py-2 text-right">Komisi</th>
                <th className="px-3 py-2 text-right">Tabungan 20%</th>
                <th className="px-3 py-2 text-right border-l">Total Gaji + Komisi</th>
                <th className="px-3 py-2 text-right">{colLabel4}</th>
                <th className="px-3 py-2 text-right">Piutang</th>
                <th className="px-3 py-2 text-right border-l">Gaji Diterima</th>
                <th className="px-3 py-2 text-left">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.name + idx} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{rp(r.pokok)}</td>
                  <td className="px-3 py-2 text-right text-pink-600">{r.komisi > 0 ? rp(r.komisi) : '-'}</td>
                  <td className="px-3 py-2 text-right text-red-500">{r.tabungan > 0 ? `- ${rp(r.tabungan)}` : '-'}</td>
                  <td className="px-3 py-2 text-right font-medium border-l">{rp(r.totalGajiKomisi)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">{r.lembur > 0 ? rp(r.lembur) : '-'}</td>
                  <td className="px-3 py-2 text-right text-red-500">{r.piutang > 0 ? `- ${rp(r.piutang)}` : '-'}</td>
                  <td className="px-3 py-2 text-right font-bold border-l bg-gray-50/50">{rp(r.gajiDiterima)}</td>
                  <td className="px-3 py-2 text-gray-400">{r.keterangan}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                <td className="px-3 py-2" colSpan={2}>TOTAL {title}</td>
                <td className="px-3 py-2 text-right">{rp(sub.pokok)}</td>
                <td className="px-3 py-2 text-right">{rp(sub.komisi)}</td>
                <td className="px-3 py-2 text-right">{rp(sub.tabungan)}</td>
                <td className="px-3 py-2 text-right border-l">{rp(sub.totalGajiKomisi)}</td>
                <td className="px-3 py-2 text-right">{rp(sub.lembur)}</td>
                <td className="px-3 py-2 text-right">{rp(sub.piutang)}</td>
                <td className="px-3 py-2 text-right border-l">{rp(sub.gajiDiterima)}</td>
                <td className="px-3 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <p className="text-emerald-700 text-sm font-bold mb-1 flex items-center gap-2">
             <Wallet size={16}/> Total Payroll Dibayarkan ({displayLabel})
          </p>
          <h3 className="text-3xl font-black text-emerald-900">{rp(grandTotals.dibayarkan)}</h3>
          {(!startDate && !endDate) && (
            <p className="text-xs text-emerald-700 mt-1.5 opacity-90 font-medium bg-emerald-100/50 inline-block px-2 py-1 rounded">
               * Siklus gaji dan komisi dihitung otomatis dari tgl 22 bulan lalu s/d tgl 21 bulan ini.
            </p>
          )}
        </div>
        <button
          onClick={handleExportExcel}
          disabled={exporting || loading}
          className="flex items-center gap-2 bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap self-start sm:self-center"
        >
          <ReceiptIcon size={16} /> {exporting ? 'Membuat file...' : 'Export Excel'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow">Memuat data...</div>
      ) : (
        <>
          <DivisiTable title="THERAPIST" rows={groups.THERAPIST} colorClass="bg-teal-50 text-teal-800" colLabel4="Bonus Tamu" />
          <DivisiTable title="GRO & KASIR" rows={groups['GRO & KASIR']} colorClass="bg-pink-50 text-pink-800" />
          <DivisiTable title="ADMIN" rows={groups.ADMIN} colorClass="bg-blue-50 text-blue-800" />
          <DivisiTable title="HOUSEKEEPING" rows={groups.HOUSEKEEPING} colorClass="bg-indigo-50 text-indigo-800" />
          <DivisiTable title="SECURITY" rows={groups.SECURITY} colorClass="bg-slate-50 text-slate-800" />
          <DivisiTable title="LAINNYA" rows={groups.LAINNYA} colorClass="bg-gray-50 text-gray-800" />

          {/* ===== TOTAL PENGELUARAN GAJI - SEMUA DIVISI ===== */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
            <div className="p-4 font-bold text-sm bg-gray-900 text-white">TOTAL PENGELUARAN GAJI — SEMUA DIVISI</div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-400 text-xs">Total Gaji Pokok</p><p className="font-bold">{rp(grandTotals.pokok)}</p></div>
              <div><p className="text-gray-400 text-xs">Total Komisi</p><p className="font-bold">{rp(grandTotals.komisi)}</p></div>
              <div><p className="text-gray-400 text-xs">Total Lembur + Bonus Tamu</p><p className="font-bold">{rp(grandTotals.lembur)}</p></div>
              <div><p className="text-gray-400 text-xs">Total Gaji + Komisi</p><p className="font-bold">{rp(grandTotals.totalGajiKomisi)}</p></div>
              <div><p className="text-gray-400 text-xs">Total Tabungan 20% (Terapis)</p><p className="font-bold text-red-500">- {rp(grandTotals.tabungan)}</p></div>
              <div><p className="text-gray-400 text-xs">Total Piutang</p><p className="font-bold text-red-500">- {rp(grandTotals.piutang)}</p></div>
              <div className="col-span-2 sm:col-span-2 bg-emerald-50 rounded-lg px-3 py-2"><p className="text-emerald-700 text-xs font-semibold">TOTAL DIBAYARKAN</p><p className="font-black text-emerald-900 text-lg">{rp(grandTotals.dibayarkan)}</p></div>
            </div>
            <p className="px-4 pb-3 text-[11px] text-gray-400">* Piutang (kasbon) per orang belum tersambung ke tabel kasbon_transactions, saat ini tertulis 0.</p>
          </div>

          {/* ===== CONTROL BONUS TAMU ===== */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
            <div className="px-4 py-3 flex items-center gap-2 font-bold text-sm">
              <Award size={16} className="text-amber-500" /> Control Bonus Tamu ({displayLabel})
            </div>
            <p className="px-4 pb-2 -mt-2 text-xs text-gray-400">Jenjang: peringkat 1-4 Rp1.250.000, peringkat 5-7 Rp750.000, peringkat 8-10 Rp250.000. Berdasarkan ranking yang sama dengan "Terapis Terbaik" di Dashboard. Nilainya sudah termasuk di kolom "Bonus Tamu" pada tabel THERAPIST di atas -- tabel ini hanya referensi, bukan tambahan.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">No</th>
                    <th className="px-3 py-2 text-left">Nama</th>
                    <th className="px-3 py-2 text-right">Total Tamu</th>
                    <th className="px-3 py-2 text-right">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {bonusTamuRows.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Belum ada data tamu periode ini</td></tr>
                  ) : bonusTamuRows.map((r, idx) => (
                    <tr key={r.name} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 text-right">{r.count}</td>
                      <td className="px-3 py-2 text-right font-semibold text-amber-600">{rp(r.bonus)}</td>
                    </tr>
                  ))}
                  {bonusTamuRows.length > 0 && (
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <td className="px-3 py-2" colSpan={3}>TOTAL BONUS TAMU</td>
                      <td className="px-3 py-2 text-right">{rp(totalBonusTamu)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== CONTROL KOMISI SUPPLIER ===== */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-sm">
                <Users size={16} className="text-indigo-500" /> Control Komisi Supplier
              </div>
              <button onClick={() => setShowSupplierForm(v => !v)} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                <Plus size={14} /> Tambah Referral
              </button>
            </div>
            <p className="px-4 pb-2 -mt-1 text-xs text-gray-400">Jatuh tempo otomatis 1 bulan sejak tanggal masuk terapis yang direferensikan. Tidak terikat filter bulan di atas.</p>

            {showSupplierForm && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b pb-4">
                <div>
                  <label className="text-xs text-gray-500">Nama Supplier (perekrut)</label>
                  <input value={supplierForm.supplier_name} onChange={e => setSupplierForm(f => ({ ...f, supplier_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Nama Terapis Direferensikan</label>
                  <input value={supplierForm.referred_name} onChange={e => setSupplierForm(f => ({ ...f, referred_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Tanggal Masuk</label>
                  <input type="date" value={supplierForm.join_date} onChange={e => setSupplierForm(f => ({ ...f, join_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Nominal Komisi</label>
                  <input type="number" value={supplierForm.commission_amount} onChange={e => setSupplierForm(f => ({ ...f, commission_amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500">Catatan (opsional)</label>
                  <input value={supplierForm.note} onChange={e => setSupplierForm(f => ({ ...f, note: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                {supplierError && <p className="sm:col-span-2 text-xs text-red-500">{supplierError}</p>}
                <div className="sm:col-span-2">
                  <button disabled={supplierSubmitting} onClick={handleSubmitSupplier} className="bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    {supplierSubmitting ? 'Menyimpan...' : 'Simpan'}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">No</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Terapis</th>
                    <th className="px-3 py-2 text-left">Tanggal Masuk</th>
                    <th className="px-3 py-2 text-right">Komisi</th>
                    <th className="px-3 py-2 text-left">Jatuh Tempo</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Catatan</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {supplierRows.length === 0 ? (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Belum ada data referral</td></tr>
                  ) : supplierRows.map((r, idx) => (
                    <tr key={r.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium">{r.supplier_name}</td>
                      <td className="px-3 py-2">{r.referred_name}</td>
                      <td className="px-3 py-2">{r.join_date}</td>
                      <td className="px-3 py-2 text-right">{rp(r.commission_amount)}</td>
                      <td className="px-3 py-2">{r.jatuhTempoStr}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${r.status === 'LUNAS' ? 'bg-emerald-100 text-emerald-700' : r.status === 'JATUH TEMPO' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{r.note || '-'}</td>
                      <td className="px-3 py-2">
                        {r.status !== 'LUNAS' && (
                          <button onClick={() => handleTogglePaidSupplier(r)} className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-800">Tandai Lunas</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {supplierRows.length > 0 && (
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <td className="px-3 py-2" colSpan={4}>TOTAL KOMISI SUPPLIER (semua)</td>
                      <td className="px-3 py-2 text-right">{rp(supplierAllTotal)}</td>
                      <td className="px-3 py-2" colSpan={4}>Sudah jatuh tempo: {rp(supplierJatuhTempoTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== HASIL CHECK / RECHECK ===== */}
          <div className="bg-gray-900 text-white rounded-lg shadow overflow-hidden mb-4">
            <div className="p-4 font-bold text-sm">Hasil Check / Recheck</div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-400 text-xs">Payroll Dibayarkan (termasuk Bonus Tamu)</p><p className="font-bold">{rp(grandTotals.dibayarkan)}</p></div>
              <div><p className="text-gray-400 text-xs">- di antaranya Bonus Tamu</p><p className="font-bold text-gray-400">{rp(totalBonusTamu)}</p></div>
              <div><p className="text-gray-400 text-xs">Supplier Sudah Jatuh Tempo</p><p className="font-bold">{rp(supplierJatuhTempoTotal)}</p></div>
              <div className="bg-emerald-600/20 rounded-lg px-3 py-2"><p className="text-emerald-300 text-xs font-semibold">TOTAL UANG KELUAR</p><p className="font-black text-lg">{rp(totalUangKeluar)}</p></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RetensiDanaTab() {
  const [saldoList, setSaldoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cairkanTarget, setCairkanTarget] = useState(null); 
  const [setNonaktifJuga, setSetNonaktifJuga] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchSaldo = async () => {
    setLoading(true);
    
    // 1. Ambil data master terapis
    const { data: therapists } = await supabase.from('therapists').select('id, name, is_active').order('name');

    // 2. Ambil SELURUH komisi terapis sejak awal (tanpa filter bulan)
    const { data: commissions } = await supabase
      .from('payroll_commissions')
      .select('staff_name, staff_role, commission_amount')
      .not('staff_role', 'ilike', '%gro%'); 

    // 3. Ambil riwayat pencairan dari tabel retensi
    const { data: pencairanData } = await supabase
      .from('therapist_retention_fund')
      .select('therapist_name, amount')
      .eq('type', 'pencairan');

    const komisiMap = {};
    (commissions || []).forEach(c => {
      const key = (c.staff_name || '').trim().toLowerCase();
      komisiMap[key] = (komisiMap[key] || 0) + Number(c.commission_amount || 0);
    });

    const pencairanMap = {};
    (pencairanData || []).forEach(p => {
      const key = (p.therapist_name || '').trim().toLowerCase();
      pencairanMap[key] = (pencairanMap[key] || 0) + Number(p.amount || 0);
    });

    const combined = (therapists || []).map(t => {
      const key = (t.name || '').trim().toLowerCase();
      const totalKomisiSeumurHidup = komisiMap[key] || 0;
      
      const totalDitahan = totalKomisiSeumurHidup * 0.2; // Dinamis 20%
      const totalDicairkan = pencairanMap[key] || 0;
      const saldoTertahan = totalDitahan - totalDicairkan;

      return {
        therapist_id: t.id,
        therapist_name: t.name,
        is_active: t.is_active,
        total_ditahan: totalDitahan,
        total_dicairkan: totalDicairkan,
        saldo_tertahan: saldoTertahan
      };
    });

    combined.sort((a, b) => {
      if (a.is_active === b.is_active) return a.therapist_name.localeCompare(b.therapist_name);
      return a.is_active ? -1 : 1;
    });

    setSaldoList(combined);
    setLoading(false);
  };

  useEffect(() => {
    fetchSaldo();
    const sub = supabase.channel('retensi-saldo-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'therapist_retention_fund' }, () => fetchSaldo())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => fetchSaldo())
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, []);

  const handleCairkan = async () => {
    if (!cairkanTarget) return;
    setSubmitting(true);

    const { error } = await supabase.from('therapist_retention_fund').insert({
      therapist_id: cairkanTarget.therapist_id,
      therapist_name: cairkanTarget.therapist_name,
      transaction_date: new Date().toISOString().split('T')[0],
      type: 'pencairan',
      amount: cairkanTarget.saldo_tertahan,
      note: 'Pencairan dana tertahan',
    });

    if (!error && setNonaktifJuga) {
      await supabase.from('therapists').update({ is_active: false }).eq('id', cairkanTarget.therapist_id);
    }

    setSubmitting(false);
    if (error) {
      alert('Gagal mencairkan: ' + error.message);
      return;
    }
    setCairkanTarget(null);
    setSetNonaktifJuga(false);
    fetchSaldo();
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden mt-2">
      <div className="p-6 pb-2">
        <h2 className="text-lg font-bold">Potongan 20% Terapis (Akumulasi Keseluruhan)</h2>
        <p className="text-xs text-gray-500 mt-1">
          * Tabel ini <b>tidak dipengaruhi oleh filter bulan</b>. Data di bawah adalah akumulasi keseluruhan sejak hari pertama (20% dari total seluruh komisi dikurangi dana yang pernah dicairkan).
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Memuat data...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Terapis</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-right">Total Ditahan</th>
                <th className="px-4 py-2 text-right">Total Dicairkan</th>
                <th className="px-4 py-2 text-right">Saldo Tertahan</th>
                <th className="px-4 py-2 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {saldoList.map(r => (
                <tr key={r.therapist_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{r.therapist_name}</td>
                  <td className="px-4 py-2 text-center">
                    {r.is_active ? (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Aktif</span>
                    ) : Number(r.saldo_tertahan) > 0 ? (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">Resign — Belum Dicairkan</span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-500">Resign</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">Rp {Number(r.total_ditahan).toLocaleString('id-ID')}</td>
                  <td className="px-4 py-2 text-right text-gray-600">Rp {Number(r.total_dicairkan).toLocaleString('id-ID')}</td>
                  <td className="px-4 py-2 text-right font-bold text-gray-900">Rp {Number(r.saldo_tertahan).toLocaleString('id-ID')}</td>
                  <td className="px-4 py-2 text-center">
                    {Number(r.saldo_tertahan) > 0 && (
                      <button
                        onClick={() => setCairkanTarget(r)}
                        className="text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 px-3 py-1 rounded-md"
                      >
                        Cairkan
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {saldoList.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Belum ada data retensi</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal konfirmasi Cairkan */}
      {cairkanTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold mb-1">Cairkan Dana Tertahan</h3>
            <p className="text-sm text-gray-500 mb-4">{cairkanTarget.therapist_name}</p>
            <div className="bg-blue-50 rounded-lg p-4 mb-4 text-center">
              <p className="text-xs text-blue-600 mb-1">Nominal yang akan dicairkan</p>
              <p className="text-2xl font-bold text-blue-900">Rp {Number(cairkanTarget.saldo_tertahan).toLocaleString('id-ID')}</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-5">
              <input type="checkbox" checked={setNonaktifJuga} onChange={(e) => setSetNonaktifJuga(e.target.checked)} />
              Sekalian set status terapis jadi Non-Aktif
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => { setCairkanTarget(null); setSetNonaktifJuga(false); }}
                className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2 text-sm font-semibold hover:bg-gray-50"
              >
                Batal
              </button>
              <button
                onClick={handleCairkan}
                disabled={submitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? 'Memproses...' : 'Konfirmasi Cairkan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== PAYROLL PAGE ==========
const getRoleBadgeColor = (role, itemName = '') => {
  const text = (itemName || role || '').toLowerCase();
  if (text.includes('gro')) return 'bg-blue-100 text-blue-700';
  if (text.includes('extend')) return 'bg-orange-100 text-orange-700';
  if (text.includes('lulur') || text.includes('scrub')) return 'bg-pink-100 text-pink-700';
  if (text.includes('stone')) return 'bg-red-100 text-red-700';
  if (text.includes('f&b') || text.includes('bonus')) return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
};

function PayrollPage({ month, setMonth, year, setYear, startDate, setStartDate, endDate, setEndDate }) {
  const [loading, setLoading] = useState(false);
  const [payrollData, setPayrollData] = useState([]);

  const [expandedRows, setExpandedRows] = useState([]);
  const [rawTransactions, setRawTransactions] = useState([]);

  const toggleRow = (staffName) => {
    setExpandedRows(prev =>
      prev.includes(staffName) ? prev.filter(n => n !== staffName) : [...prev, staffName]
    );
  };

  const handlePrintSlip = (staffRow, staffHistory) => {
    const displayLabel = getPayrollDisplayLabel(startDate, endDate, month, year);
    const printWindow = window.open('', '_blank');
    
    const tableRows = staffHistory.length === 0 
      ? `<tr><td colspan="4" style="text-align:center; padding: 20px; color: #999;">Belum ada riwayat transaksi detail.</td></tr>`
      : staffHistory.map(hist => {
          const dateStr = new Date(hist.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
          
          let layananHtml = '';
          let nominalHtml = '';

          if (hist.item_breakdown) {
            layananHtml = hist.item_breakdown.map(it => `<div style="margin-bottom: 4px;">${it.name}</div>`).join('');
            nominalHtml = hist.item_breakdown.map(it => `<div style="margin-bottom: 4px;">Rp ${it.amount.toLocaleString('id-ID')}</div>`).join('');
          } else {
            layananHtml = `${hist.display_role} (${hist.commission_percentage}%)`;
            nominalHtml = `Rp ${Number(hist.commission_amount).toLocaleString('id-ID')}`;
          }

          return `
            <tr>
              <td>${dateStr}</td>
              <td><b>${hist.display_guest}</b><br/><span style="color:#666; font-size:12px;">${hist.display_room}</span></td>
              <td class="center">${layananHtml}</td>
              <td class="right" style="color: #1d4ed8; font-weight: bold;">${nominalHtml}</td>
            </tr>
          `;
        }).join('');

    const html = `
      <html>
        <head>
          <title>Slip Komisi - ${staffRow.name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px; }
            .header h2 { margin: 0 0 5px 0; color: #1e3a8a; font-size: 24px; text-transform: uppercase; letter-spacing: 1px; }
            .header p { margin: 0; color: #64748b; font-size: 14px; }
            .info-box { display: flex; justify-content: space-between; margin-bottom: 20px; background: #f8fafc; padding: 15px 20px; border-radius: 8px; border: 1px solid #e2e8f0; }
            .info-box div { font-size: 13px; color: #64748b; }
            .info-box span { font-weight: bold; font-size: 16px; display: block; margin-top: 5px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 13px; }
            th { background-color: #f1f5f9; text-align: left; padding: 12px 10px; border-bottom: 2px solid #cbd5e1; color: #475569; font-weight: 600; text-transform: uppercase; font-size: 12px; }
            td { padding: 12px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
            tr:nth-child(even) { background-color: #f8fafc; }
            th.right, td.right { text-align: right; }
            th.center, td.center { text-align: center; }
            .footer { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; padding-top: 15px; border-top: 2px solid #cbd5e1; }
            .footer-note { font-size: 12px; color: #94a3b8; font-style: italic; }
            .footer-total { text-align: right; font-size: 16px; font-weight: bold; color: #334155; }
            .footer-total span { color: #1d4ed8; font-size: 22px; margin-left: 10px; }
            @media print {
              body { padding: 0; max-width: 100%; }
              .info-box { border: 1px solid #000; }
              th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>ATLAS Men's Health</h2>
            <p>Rincian Komisi Pelayanan (${displayLabel})</p>
          </div>
          
          <div class="info-box">
            <div>
              Nama Staff:
              <span>${staffRow.name} (${staffRow.category === 'gro' ? 'GRO' : 'Terapis'})</span>
            </div>
            <div>
              Jumlah Tamu:
              <span>${staffRow.treatmentCount} Tamu</span>
            </div>
            <div>
              Total Revenue:
              <span>Rp ${staffRow.totalRevenue.toLocaleString('id-ID')}</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 18%;">Tanggal & Waktu</th>
                <th style="width: 32%;">Tamu & Kamar</th>
                <th class="center" style="width: 25%;">Peran / Layanan</th>
                <th class="right" style="width: 25%;">Nominal Komisi</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="footer">
            <div class="footer-note">* Dokumen ini dicetak otomatis oleh sistem ATLAS.</div>
            <div class="footer-total">
              Total Komisi Diterima: <span>Rp ${staffRow.totalCommission.toLocaleString('id-ID')}</span>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const fetchPayrollData = async () => {
    setLoading(true);

    // PENGGUNAAN SIKLUS PAYROLL (22 s/d 21)
    const { startLocal, endLocal } = getPayrollPeriodRange(month, year, startDate, endDate);
    const { startStr: startMonthStr, endStr: endMonthStr } = toLocalRangeStrings(startLocal, endLocal);

    const { data: rawCommissions, error } = await supabase
      .from('payroll_commissions')
      .select('*')
      .gte('created_at', startMonthStr)
      .lte('created_at', endMonthStr)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[Payroll] gagal ambil komisi:', error.message);
      setPayrollData([]);
      setRawTransactions([]);
      setLoading(false);
      return;
    }

    const { data: orderItemsData } = await supabase
      .from('order_items')
      .select('order_id, item_name, subtotal, item_kind, therapists(name), treatments(category)')
      .gte('created_at', startMonthStr)
      .lte('created_at', endMonthStr);

    const itemsGrouped = {};
    const itemsGroupedByOrder = {};
    (orderItemsData || []).forEach(oi => {
      const sName = oi.therapists?.name;
      if (oi.item_kind !== 'treatment') return;

      if (sName) {
        const key = `${oi.order_id}_${sName}`;
        if (!itemsGrouped[key]) itemsGrouped[key] = [];
        itemsGrouped[key].push({ name: oi.item_name, subtotal: Number(oi.subtotal), isAddon: oi.treatments?.category === 'addon', used: false });
      }

      if (!itemsGroupedByOrder[oi.order_id]) itemsGroupedByOrder[oi.order_id] = [];
      itemsGroupedByOrder[oi.order_id].push({ name: oi.item_name, subtotal: Number(oi.subtotal), isAddon: oi.treatments?.category === 'addon', used: false });
    });

    const { data: billsData } = await supabase
      .from('bills')
      .select('order_id, room_name, guest_name')
      .gte('checked_out_at', startMonthStr)
      .lte('checked_out_at', endMonthStr);

    const billsMap = {};
    (billsData || []).forEach(b => {
      if (b.order_id) billsMap[b.order_id] = b;
    });

    const enrichedCommissions = (rawCommissions || []).map(comm => {
      const mapKey = `${comm.order_id}_${comm.staff_name}`;
      let refinedRole = comm.staff_role;
      let matchedItems = [];

      const isTherapistRole = comm.staff_role === 'Therapist' || comm.staff_role === 'TS-Terapis';
      const isGroRole = (comm.staff_role || '').toUpperCase().includes('GRO');

      let availableItems = null;
      if (isTherapistRole && itemsGrouped[mapKey]) {
        availableItems = itemsGrouped[mapKey];
      } else if (isGroRole && itemsGroupedByOrder[comm.order_id]) {
        availableItems = itemsGroupedByOrder[comm.order_id];
      }

      if (availableItems) {
        const targetAmount = Number(comm.base_amount);

        const exactIdx = availableItems.findIndex(i => i.subtotal === targetAmount && !i.used);
        if (exactIdx !== -1) {
          refinedRole = availableItems[exactIdx].name;
          availableItems[exactIdx].used = true;
          matchedItems = [{ name: availableItems[exactIdx].name, subtotal: availableItems[exactIdx].subtotal, isAddon: availableItems[exactIdx].isAddon }];
        } else {
          let currentSum = 0;
          let usedIndices = [];
          for (let i = 0; i < availableItems.length; i++) {
            if (!availableItems[i].used) {
              currentSum += availableItems[i].subtotal;
              usedIndices.push(i);
              if (currentSum === targetAmount) {
                refinedRole = usedIndices.map(idx => availableItems[idx].name).join(' + ');
                matchedItems = usedIndices.map(idx => ({ name: availableItems[idx].name, subtotal: availableItems[idx].subtotal, isAddon: availableItems[idx].isAddon }));
                usedIndices.forEach(idx => availableItems[idx].used = true);
                break;
              }
            }
          }
        }
      }

      const pct = Number(comm.commission_percentage || 0) / 100;
      let itemBreakdown = null;
      if (matchedItems.length > 1) {
        const regularSum = matchedItems
          .filter(it => !it.isAddon)
          .reduce((sum, it) => sum + Math.round(it.subtotal * pct), 0);
        const addonItems = matchedItems.filter(it => it.isAddon);
        const addonBase = addonItems.reduce((sum, it) => sum + it.subtotal, 0);
        const addonPool = Math.max(0, Math.round(Number(comm.commission_amount || 0)) - regularSum);
        const lastAddonIdx = matchedItems.reduce((last, it, idx) => (it.isAddon ? idx : last), -1);
        let remaining = addonPool;

        itemBreakdown = matchedItems.map((it, idx) => {
          if (!it.isAddon) return { name: it.name, amount: Math.round(it.subtotal * pct) };
          const amount = idx === lastAddonIdx
            ? remaining
            : Math.round(addonPool * it.subtotal / (addonBase || 1));
          remaining -= amount;
          return { name: it.name, amount };
        });
      }

      return {
        ...comm,
        display_role: refinedRole,
        display_guest: billsMap[comm.order_id]?.guest_name || 'Tamu',
        display_room: billsMap[comm.order_id]?.room_name || 'Tanpa Kamar',
        item_breakdown: itemBreakdown,
      };
    });

    setRawTransactions(enrichedCommissions);

    const stats = {};
    const orderIdsPerStaff = {};

    enrichedCommissions.forEach(comm => {
      const staffName = comm.staff_name;
      if (!stats[staffName]) {
        stats[staffName] = {
          id: staffName,
          name: staffName,
          treatmentCount: 0,
          totalRevenue: 0,
          rankRevenue: 0, // revenue treatment saja (tanpa Bonus F&B), dipakai untuk pengurutan
          totalCommission: 0,
          breakdown: {},
          category: (comm.staff_role || '').toUpperCase().includes('GRO') ? 'gro' : 'terapis'
        };
        orderIdsPerStaff[staffName] = new Set();
      }

      // Order yang hanya berisi Bonus F&B (tamu cuma pesan makanan/minuman, tanpa treatment)
      // tidak dihitung sebagai tamu. Komisinya tetap masuk ke total komisi.
      const isFnbBonusOnly = (comm.staff_role || '').toLowerCase().includes('bonus f&b');
      if (!isFnbBonusOnly && !orderIdsPerStaff[staffName].has(comm.order_id)) {
        orderIdsPerStaff[staffName].add(comm.order_id);
        stats[staffName].treatmentCount += 1;
      }

      stats[staffName].totalRevenue += Number(comm.base_amount || 0);
      if (!isFnbBonusOnly) stats[staffName].rankRevenue += Number(comm.base_amount || 0);
      stats[staffName].totalCommission += Number(comm.commission_amount || 0);

      const roleName = comm.display_role;
      stats[staffName].breakdown[roleName] =
        (stats[staffName].breakdown[roleName] || 0) + Number(comm.commission_amount || 0);
    });

    // Urutan: GRO dulu, lalu terapis. Di dalam tiap kelompok, sama dengan Terapis Terbaik:
    // jumlah tamu -> revenue -> total komisi -> abjad
    const formattedData = Object.values(stats).sort((a, b) => {
      if (a.category !== b.category) return a.category === 'gro' ? -1 : 1;
      return (
        b.treatmentCount - a.treatmentCount ||
        b.rankRevenue - a.rankRevenue ||
        b.totalCommission - a.totalCommission ||
        a.name.localeCompare(b.name)
      );
    });

    setPayrollData(formattedData);
    setLoading(false);
  };

  useEffect(() => {
    fetchPayrollData();

    const commissionSub = supabase.channel('payroll-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => { fetchPayrollData(); })
      .subscribe();

    return () => supabase.removeChannel(commissionSub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, startDate, endDate]);

  const totalPayroll = payrollData.reduce((sum, item) => sum + item.totalCommission, 0);
  const displayLabel = getPayrollDisplayLabel(startDate, endDate, month, year);

  return (
    <div className="space-y-6">
      <FilterBar 
        month={month} setMonth={setMonth} 
        year={year} setYear={setYear} 
        startDate={startDate} setStartDate={setStartDate} 
        endDate={endDate} setEndDate={setEndDate} 
      />

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-5 shadow-sm">
        <p className="text-blue-600 text-sm font-semibold mb-1">Total Estimasi Komisi Terapis & GRO ({displayLabel})</p>
        <h3 className="text-3xl font-bold text-blue-900">Rp {totalPayroll.toLocaleString('id-ID')}</h3>
        {(!startDate && !endDate) && (
          <p className="text-xs text-blue-700 mt-1.5 opacity-90 font-medium bg-blue-100/50 inline-block px-2 py-1 rounded">
             * Siklus komisi dihitung otomatis dari tgl 22 bulan lalu s/d tgl 21 bulan ini.
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden border">
        {loading ? (
          <div className="py-12 text-center text-gray-500 animate-pulse">Menghitung komisi...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-700">Nama Staff</th>
                  <th className="px-6 py-3 text-right font-semibold text-gray-700">Jumlah Tamu</th>
                  <th className="px-6 py-3 text-right font-semibold text-gray-700">Revenue (Rp)</th>
                  <th className="px-6 py-3 text-right font-semibold text-blue-700">Total Komisi (Rp)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payrollData.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-gray-400">Tidak ada data transaksi di periode ini.</td></tr>
                ) : (
                  payrollData.map((row, idx) => {
                    const prevRow = payrollData[idx - 1];
                    const showDivider = idx > 0 && prevRow && prevRow.category !== row.category;
                    const isExpanded = expandedRows.includes(row.name);

                    const staffHistory = rawTransactions.filter(t => t.staff_name === row.name);

                    return (
                      <React.Fragment key={row.id}>
                        {showDivider && (
                          <tr><td colSpan="4" className="p-0"><div className="border-t-2 border-blue-200"></div></td></tr>
                        )}

                        <tr
                          onClick={() => toggleRow(row.name)}
                          className={`transition-colors cursor-pointer border-b border-gray-100 ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-6 py-4 font-medium text-gray-900 flex items-center select-none">
                            <button className="mr-2 text-gray-500 hover:text-blue-600 focus:outline-none">
                              {isExpanded ? <ChevronDown size={18} className="text-blue-600" /> : <ChevronRight size={18} />}
                            </button>
                            <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mr-3 ${row.category === 'gro' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                              {row.category === 'gro' ? 'GRO' : 'TERAPIS'}
                            </span>
                            {row.name}
                          </td>
                          <td className="px-6 py-4 text-right select-none">{row.treatmentCount} Tamu</td>
                          <td className="px-6 py-4 text-right text-gray-600 select-none">{row.totalRevenue.toLocaleString('id-ID')}</td>
                          <td className="px-6 py-4 text-right font-bold text-gray-900 bg-gray-50/50 select-none">
                            {row.totalCommission.toLocaleString('id-ID')}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-gray-50/70 border-b border-gray-200">
                            <td colSpan="4" className="px-10 py-4">
                              <div className="text-xs bg-white border rounded-lg shadow-sm overflow-hidden">
                                
                                {/* Header Rincian + Tombol Print */}
                                <div className="flex justify-between items-center bg-gray-100 px-4 py-2 border-b">
                                  <span className="font-bold text-gray-700">Rincian Komisi: {row.name}</span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handlePrintSlip(row, staffHistory); }}
                                    className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-blue-600 px-3 py-1.5 rounded text-xs font-semibold shadow-sm transition-colors"
                                  >
                                    <Printer size={14} /> Cetak Slip
                                  </button>
                                </div>

                                <table className="w-full text-left">
                                  <thead className="bg-gray-50 text-gray-500 border-b">
                                    <tr>
                                      <th className="py-2 px-4 font-semibold w-32">Tanggal</th>
                                      <th className="py-2 px-4 font-semibold">Tamu & Kamar</th>
                                      <th className="py-2 px-4 font-semibold text-center">Peran / Layanan</th>
                                      <th className="py-2 px-4 font-semibold text-right text-blue-700">Nominal Komisi</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {staffHistory.length === 0 ? (
                                      <tr><td colSpan="4" className="py-3 px-4 text-center italic text-gray-400">Belum ada riwayat transaksi detail yang terekam.</td></tr>
                                    ) : (
                                      staffHistory.map(hist => (
                                        <tr key={hist.id} className="border-b border-gray-50 hover:bg-gray-50">
                                          <td className="py-2 px-4 text-gray-500">
                                            {new Date(hist.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                          </td>
                                          <td className="py-2 px-4 text-gray-700">
                                            <span className="font-semibold">{hist.display_guest}</span>
                                            <span className="text-gray-400"> — {hist.display_room}</span>
                                          </td>
                                          <td className="py-2 px-4 text-center">
                                            {hist.item_breakdown ? (
                                              <div className="space-y-1">
                                                {hist.item_breakdown.map((it, i) => (
                                                  <div key={i} className="flex items-center justify-center min-h-[28px]">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${getRoleBadgeColor(it.name, it.name)}`}>
                                                      {it.name}
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded font-semibold ${getRoleBadgeColor(hist.display_role, hist.display_role)}`}>
                                                {hist.display_role} ({hist.commission_percentage}%)
                                              </span>
                                            )}
                                          </td>
                                          <td className="py-2 px-4 text-right font-bold text-blue-700">
                                            {hist.item_breakdown ? (
                                              <div className="space-y-1">
                                                {hist.item_breakdown.map((it, i) => (
                                                  <div key={i} className="flex items-center justify-end min-h-[28px]">
                                                    <span>Rp {it.amount.toLocaleString('id-ID')}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              `Rp ${Number(hist.commission_amount).toLocaleString('id-ID')}`
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== EXPENSES PAGE ==========
function ExpensesPage({ month, setMonth, year: yearFilter, setYear: setYearFilter, startDate, setStartDate, endDate, setEndDate }) {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [fnbItems, setFnbItems] = useState([]);
  const [showRestockForm, setShowRestockForm] = useState(false);

  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  const [restockQty, setRestockQty] = useState('');
  const [restockAmount, setRestockAmount] = useState('');
  const [restockNote, setRestockNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [staffList, setStaffList] = useState([]);
  const [lemburLogs, setLemburLogs] = useState([]);
  
  const [groCommissions, setGroCommissions] = useState({});
  const [totalTerapisPayroll, setTotalTerapisPayroll] = useState(0); 

  const [showLemburForm, setShowLemburForm] = useState(false);
  const [lemburTanggal, setLemburTanggal] = useState(new Date().toISOString().split('T')[0]);
  const [gajiStaffId, setGajiStaffId] = useState('');
  const [lemburJam, setLemburJam] = useState('');
  const [gajiNote, setGajiNote] = useState('');

  useEffect(() => {
    fetchExpenses();
    fetchLemburLogs();
    fetchPayrollData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, yearFilter, startDate, endDate]);

  useEffect(() => {
    supabase.from('menu_items').select('*').eq('track_stock', true).order('name').then(({ data }) => {
      setFnbItems(data || []);
    });
    supabase.from('staff').select('*').eq('is_active', true).order('created_at').then(({ data, error }) => {
      if (error) { console.error('[Expenses] gagal ambil data staff:', error.message); return; }
      setStaffList(data || []);
    });
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('expenses-page-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lembur_logs' }, () => fetchLemburLogs())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => fetchPayrollData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, yearFilter, startDate, endDate]);

  const getMonthBoundaries = () => {
    // PENGGUNAAN SIKLUS KALENDER MURNI
    const { startLocal, endLocal } = getCalendarPeriodRange(month, yearFilter, startDate, endDate);
    return {
      startStr: startLocal.toISOString(),
      endStr: endLocal.toISOString(),
      startDateOnly: toLocalDateStr(startLocal),
      endDateOnly: toLocalDateStr(endLocal),
    };
  };

  const fetchExpenses = async () => {
    setLoading(true);
    const { startStr, endStr } = getMonthBoundaries();
    const { data } = await supabase.from('expenses').select('*, menu_items(name, unit)').gte('created_at', startStr).lte('created_at', endStr).order('created_at', { ascending: false });
    setExpenses(data || []);
    setLoading(false);
  };

  const fetchLemburLogs = async () => {
    const { startDateOnly, endDateOnly } = getMonthBoundaries();
    const { data, error } = await supabase.from('lembur_logs').select('*').gte('tanggal', startDateOnly).lte('tanggal', endDateOnly).order('tanggal', { ascending: false });
    if (error) { console.error('[Expenses] gagal ambil lembur:', error.message); return; }
    setLemburLogs(data || []);
  };

  const fetchPayrollData = async () => {
    // Komisi memakai siklus payroll 22-21, sedangkan pengeluaran lain tetap kalender
    const { startLocal: commStart, endLocal: commEnd } = getPayrollPeriodRange(month, yearFilter, startDate, endDate);

    const [commRes, therRes] = await Promise.all([
      supabase.from('payroll_commissions').select('staff_name, staff_role, commission_amount').gte('created_at', commStart.toISOString()).lte('created_at', commEnd.toISOString()),
      supabase.from('therapists').select('*')
    ]);

    const komisiGroMap = {};
    const komisiTerapisMap = {};
    
    (commRes.data || []).forEach(c => {
      const role = (c.staff_role || '').toLowerCase();
      const staffKey = (c.staff_name || '').trim().toLowerCase();
      if (role.includes('gro')) {
        komisiGroMap[staffKey] = (komisiGroMap[staffKey] || 0) + Number(c.commission_amount || 0);
      } else {
        komisiTerapisMap[staffKey] = (komisiTerapisMap[staffKey] || 0) + Number(c.commission_amount || 0);
      }
    });
    setGroCommissions(komisiGroMap);

    // Komisi terapis KOTOR (retensi 20% hanya ditahan, bukan pengurang biaya)
    let totalKomisiTerapis = 0;
    (therRes.data || []).filter(t => t.is_active).forEach(t => {
      const tKey = (t.name || '').trim().toLowerCase();
      totalKomisiTerapis += komisiTerapisMap[tKey] || 0;
    });

    setTotalTerapisPayroll(totalKomisiTerapis);
  };

  const resetForm = () => { setSelectedMenuItem(''); setRestockQty(''); setRestockAmount(''); setRestockNote(''); setFormError(''); };
  const resetLemburForm = () => { setGajiStaffId(''); setLemburJam(''); setGajiNote(''); setLemburTanggal(new Date().toISOString().split('T')[0]); setFormError(''); };

  const selectedStaff = staffList.find(s => s.id === gajiStaffId);
  const lemburAmountPreview = selectedStaff ? Math.round(Number(lemburJam || 0) * Number(selectedStaff.hourly_rate || 0) * Number(selectedStaff.overtime_multiplier || 1)) : 0;

  const handleSubmitLembur = async () => {
    if (!lemburTanggal) return setFormError('Pilih tanggal lembur');
    if (!gajiStaffId) return setFormError('Pilih staff terlebih dahulu');
    if (!lemburJam || Number(lemburJam) <= 0) return setFormError('Isi jumlah jam lembur');
    if (lemburAmountPreview <= 0) return setFormError('Tarif lembur staff belum diatur di database');

    setSubmitting(true); setFormError('');
    const { error } = await supabase.from('lembur_logs').insert({
      tanggal: lemburTanggal, nama_pegawai: selectedStaff.name, staff_id: selectedStaff.id, jam_lembur: Number(lemburJam), uang_lembur: lemburAmountPreview,
    });
    setSubmitting(false);

    if (error) return setFormError('Gagal menyimpan: ' + error.message);
    resetLemburForm(); setShowLemburForm(false); fetchLemburLogs();
  };

  const handleSubmitRestock = async () => {
    if (!selectedMenuItem) return setFormError('Pilih item FnB');
    if (!restockQty || Number(restockQty) <= 0) return setFormError('Jumlah restock > 0');
    if (!restockAmount || Number(restockAmount) <= 0) return setFormError('Isi total biaya');

    setSubmitting(true); setFormError('');
    const menuItem = fnbItems.find(m => m.id === selectedMenuItem);
    const { error } = await supabase.from('expenses').insert({
      category: 'fnb_restock', menu_item_id: selectedMenuItem, quantity: Number(restockQty), amount: Number(restockAmount), description: restockNote || `Restock ${menuItem?.name || ''}`,
    });
    setSubmitting(false);

    if (error) return setFormError('Gagal menyimpan: ' + error.message);
    resetForm(); setShowRestockForm(false); fetchExpenses();
  };

  const categoryLabel = { fnb_restock: 'Restock FnB', inventaris_operasional: 'Inventaris & Operasional', gaji: 'Gaji', lembur: 'Lembur', amortisasi: 'Amortisasi', lainnya: 'Lainnya' };
  const categoryBadgeColor = { fnb_restock: 'bg-amber-100 text-amber-800', inventaris_operasional: 'bg-blue-100 text-blue-800', gaji: 'bg-green-100 text-green-800', lembur: 'bg-orange-100 text-orange-800', amortisasi: 'bg-indigo-100 text-indigo-800', lainnya: 'bg-gray-100 text-gray-700' };

  const totalsByCategory = expenses.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + Number(e.amount || 0); return acc; }, {});
  const totalAmortisasi = totalsByCategory.amortisasi || 0;
  const totalLembur = lemburLogs.reduce((sum, l) => sum + Number(l.uang_lembur || 0), 0);
  const totalGroKomisi = Object.values(groCommissions).reduce((a, b) => a + b, 0);
  
  const totalAll = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0) + totalLembur + totalGroKomisi + totalTerapisPayroll;
  const totalKasKeluar = totalAll - totalAmortisasi;

  const displayLabel = getCalendarDisplayLabel(startDate, endDate, month, yearFilter);

  return (
    <div className="space-y-4">
      <FilterBar 
        month={month} setMonth={setMonth} 
        year={yearFilter} setYear={setYearFilter} 
        startDate={startDate} setStartDate={setStartDate} 
        endDate={endDate} setEndDate={setEndDate} 
        extraButtons={
          <>
            <button onClick={() => { setShowLemburForm(true); resetLemburForm(); }} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm"><Clock size={16} /> Catat Lembur</button>
            <button onClick={() => { setShowRestockForm(true); resetForm(); }} className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm"><Plus size={16} /> Restock FnB</button>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
        <SummaryCard label="Kas Keluar Aktual" value={totalKasKeluar} highlight />
        <SummaryCard label="Restock FnB" value={totalsByCategory.fnb_restock || 0} />
        <SummaryCard label="Inventaris & Operasional" value={totalsByCategory.inventaris_operasional || 0} />
        <SummaryCard label="Gaji, Lembur & Komisi GRO (22-21)" value={(totalsByCategory.gaji || 0) + totalLembur + totalGroKomisi} />
        <SummaryCard label="Komisi Terapis (siklus 22-21)" value={totalTerapisPayroll} />
        <SummaryCard label="Amortisasi" value={totalAmortisasi} />
        <SummaryCard label="Total Operasional" value={totalAll} highlight />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 pb-2">
          <h2 className="text-lg font-bold">Riwayat Pengeluaran ({displayLabel})</h2>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-500">Memuat data...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Tanggal</th>
                  <th className="px-4 py-2 text-left">Kategori</th>
                  <th className="px-4 py-2 text-left min-w-[200px]">Deskripsi</th>
                  <th className="px-4 py-2 text-right">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...expenses.map(e => ({ 
                    id: `exp-${e.id}`, 
                    tanggal: e.expense_date || e.created_at, 
                    kategori: e.category, 
                    deskripsi: e.description, 
                    jumlah: Number(e.amount || 0), 
                  })),
                  ...lemburLogs.map(l => {
                    const staffRole = staffList.find(s => s.id === l.staff_id || s.name === l.nama_pegawai)?.role;
                    return { 
                      id: `lembur-${l.id}`, 
                      tanggal: l.tanggal, 
                      kategori: 'lembur', 
                      deskripsi: `Lembur ${l.nama_pegawai} (${l.jam_lembur} ${staffRole === 'Security' ? 'hari' : 'jam'})`, 
                      jumlah: Number(l.uang_lembur || 0), 
                    };
                  }),
                ].sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal)).map(row => (
                  <tr key={row.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">{new Date(row.tanggal).toLocaleDateString('id-ID')}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-1 rounded text-xs font-semibold ${categoryBadgeColor[row.kategori] || categoryBadgeColor.lainnya}`}>{categoryLabel[row.kategori] || row.kategori}</span></td>
                    <td className="px-4 py-2 min-w-[200px]">{row.deskripsi}</td>
                    <td className="px-4 py-2 text-right font-semibold">Rp {row.jumlah.toLocaleString('id-ID')}</td>
                  </tr>
                ))}
                {expenses.length === 0 && lemburLogs.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Belum ada pengeluaran di periode ini</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODALS RESTOCK & LEMBUR */}
      {showRestockForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRestockForm(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-96 max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Restock FnB</h3>
              <button onClick={() => setShowRestockForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-600 block mb-1">Item FnB</label>
              <select value={selectedMenuItem} onChange={(e) => setSelectedMenuItem(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Pilih item...</option>
                {fnbItems.map(item => (<option key={item.id} value={item.id}>{item.name} (stok: {item.current_stock} {item.unit})</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-xs text-gray-600 block mb-1">Jumlah Restock</label><input type="number" min={1} value={restockQty} onChange={(e) => setRestockQty(e.target.value)} placeholder="mis. 24" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 block mb-1">Total Biaya (Rp)</label><input type="number" min={0} value={restockAmount} onChange={(e) => setRestockAmount(e.target.value)} placeholder="mis. 480000" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-600 block mb-1">Catatan (opsional)</label>
              <input type="text" value={restockNote} onChange={(e) => setRestockNote(e.target.value)} placeholder="mis. Beli dari Toko Sinar Jaya" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {formError && <p className="text-xs text-red-600 mb-3">{formError}</p>}
            <div className="flex gap-2">
              <button onClick={() => setShowRestockForm(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-semibold hover:bg-gray-50">Batal</button>
              <button onClick={handleSubmitRestock} disabled={submitting} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-2 text-sm font-semibold flex items-center justify-center gap-1">{submitting ? 'Menyimpan...' : <><Check size={14} /> Simpan</>}</button>
            </div>
          </div>
        </div>
      )}

      {showLemburForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLemburForm(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-96 max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Catat Lembur Harian</h3>
              <button onClick={() => setShowLemburForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-600 block mb-1">Tanggal Lembur</label>
              <input type="date" value={lemburTanggal} onChange={(e) => setLemburTanggal(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-600 block mb-1">Staff</label>
              <select value={gajiStaffId} onChange={(e) => setGajiStaffId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Pilih staff...</option>
                {staffList.map(s => (<option key={s.id} value={s.id}>{s.name}{s.role ? ` (${s.role})` : ''}</option>))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-600 block mb-1">Durasi Lembur ({selectedStaff?.role === 'Security' ? 'Hari' : 'Jam'})</label>
              <input type="number" min={0} step={selectedStaff?.role === 'Security' ? 1 : 0.5} value={lemburJam} onChange={(e) => setLemburJam(e.target.value)} placeholder={selectedStaff?.role === 'Security' ? 'mis. 1' : 'mis. 4'} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {selectedStaff && (
              <div className="mb-4 bg-gray-50 border rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Preview Nominal (Otomatis)</p>
                <div className="text-lg font-bold text-gray-800">Rp {lemburAmountPreview.toLocaleString('id-ID')}</div>
                <p className="text-[10px] text-gray-400 mt-1">Rp {Number(selectedStaff.hourly_rate || 0).toLocaleString('id-ID')}/jam × {selectedStaff.overtime_multiplier || 1}x</p>
              </div>
            )}
            <div className="mb-4">
              <label className="text-xs text-gray-600 block mb-1">Catatan (opsional)</label>
              <input type="text" value={gajiNote} onChange={(e) => setGajiNote(e.target.value)} placeholder="mis. Ganti shift Budi" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {formError && <p className="text-xs text-red-600 mb-3">{formError}</p>}
            <div className="flex gap-2 mt-2">
              <button onClick={() => setShowLemburForm(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-semibold hover:bg-gray-50">Batal</button>
              <button onClick={handleSubmitLembur} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg py-2 text-sm font-semibold flex items-center justify-center gap-1">{submitting ? 'Menyimpan...' : <><Check size={14} /> Simpan</>}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== LAPORAN KEUANGAN PAGE ==========
function FinancialReportPage({ month, setMonth, year: yearFilter, setYear: setYearFilter, startDate, setStartDate, endDate, setEndDate }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [hutangPiutang, setHutangPiutang] = useState(null);
  
  const [manualStats, setManualStats] = useState({
    komisiTerapis: 0, // KOTOR, sebelum potongan retensi 20%
    komisiGro: 0
  });

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);

      // PENGGUNAAN SIKLUS KALENDER MURNI (pendapatan, operasional, amortisasi)
      const { startLocal, endLocal } = getCalendarPeriodRange(month, yearFilter, startDate, endDate);

      const startMonthStr = startLocal.toISOString();
      const endMonthStr = endLocal.toISOString();

      // Komisi GRO & Terapis memakai siklus payroll 22-21, sama dengan halaman
      // Gaji & Komisi Terapis, supaya "Oktober" ikut memasukkan 22-30 September.
      const { startLocal: commStart, endLocal: commEnd } = getPayrollPeriodRange(month, yearFilter, startDate, endDate);
      const commStartStr = commStart.toISOString();
      const commEndStr = commEnd.toISOString();

      const endBulanKey = `${endLocal.getFullYear()}-${pad2(endLocal.getMonth() + 1)}-01`;

      const [
        { data: orders },
        { data: therapists },
        { data: commissions },
        { data: hpRow },
        { data: expensesData },
        { data: lemburData }
      ] = await Promise.all([
        supabase.from('orders').select('total_amount, paid_at').eq('status', 'paid').gte('paid_at', startMonthStr).lte('paid_at', endMonthStr),
        supabase.from('therapists').select('*'),
        supabase.from('payroll_commissions').select('staff_name, staff_role, commission_amount').gte('created_at', commStartStr).lte('created_at', commEndStr),
        supabase.from('v_non_biaya_bulanan').select('*').eq('bulan', endBulanKey).maybeSingle(),
        supabase.from('expenses').select('amount, category').gte('created_at', startMonthStr).lte('created_at', endMonthStr),
        supabase.from('lembur_logs').select('uang_lembur').gte('tanggal', toLocalDateStr(startLocal)).lte('tanggal', toLocalDateStr(endLocal))
      ]);

      let dynBiayaOperasional = 0;
      let dynHppFnb = 0;
      let dynGajiTetap = 0;
      let dynAmortisasi = 0;

      (expensesData || []).forEach(e => {
        const amt = Number(e.amount || 0);
        if (e.category === 'fnb_restock') dynHppFnb += amt;
        else if (e.category === 'gaji') dynGajiTetap += amt;
        else if (e.category === 'amortisasi') dynAmortisasi += amt;
        else dynBiayaOperasional += amt;
      });

      let dynLembur = 0;
      (lemburData || []).forEach(l => {
        dynLembur += Number(l.uang_lembur || 0);
      });

      let realTimeRevenue = 0;
      let realTimeCount = 0;
      
      (orders || []).forEach(o => {
          if(o.paid_at) {
              const dObj = new Date(o.paid_at);
              const dateKey = `${dObj.getFullYear()}-${String(dObj.getMonth() + 1).padStart(2, '0')}-${String(dObj.getDate()).padStart(2, '0')}`;
              
              if (!HARDCODE_DAILY[dateKey]) {
                  realTimeRevenue += Number(o.total_amount);
                  realTimeCount += 1;
              }
          }
      });
      
      let initialData = {
        pendapatan_kotor: realTimeRevenue,
        diskon: 0,
        pendapatan_bersih: realTimeRevenue,
        hpp_fnb: dynHppFnb,
        laba_kotor: realTimeRevenue - dynHppFnb,
        biaya_gaji_staff_tetap: dynGajiTetap,
        biaya_lembur: dynLembur,
        biaya_operasional: dynBiayaOperasional,
        amortisasi: dynAmortisasi,
        jumlah_transaksi: realTimeCount,
        rata_rata_transaksi: 0,
      };

      // ==========================================
      // INJEKSI HISTORICAL DATA DINAMIS
      // ==========================================
      let omzetHistory = 0;
      let tamuHistory = 0;
      let curr = new Date(startLocal);
      curr.setHours(0,0,0,0);
      const endD = new Date(endLocal);
      endD.setHours(0,0,0,0);
      
      while(curr <= endD) {
        const dateKey = `${curr.getFullYear()}-${String(curr.getMonth() + 1).padStart(2, '0')}-${String(curr.getDate()).padStart(2, '0')}`;
        if (HARDCODE_DAILY[dateKey]) {
            omzetHistory += HARDCODE_DAILY[dateKey].revenue;
            tamuHistory += HARDCODE_DAILY[dateKey].pax;
        }
        curr.setDate(curr.getDate() + 1);
      }
      
      initialData.pendapatan_kotor += omzetHistory;
      initialData.pendapatan_bersih += omzetHistory;
      initialData.laba_kotor += omzetHistory;
      initialData.jumlah_transaksi += tamuHistory;

      if (initialData.jumlah_transaksi > 0) {
          initialData.rata_rata_transaksi = Math.round(initialData.pendapatan_bersih / initialData.jumlah_transaksi);
      }

      setData(initialData);

      const komisiTerapisMap = {};
      let totalKomisiGro = 0;

      (commissions || []).forEach(c => {
        const role = (c.staff_role || '').toLowerCase();
        const staffKey = (c.staff_name || '').trim().toLowerCase();
        if (role.includes('gro')) {
          totalKomisiGro += Number(c.commission_amount || 0);
        } else {
          komisiTerapisMap[staffKey] = (komisiTerapisMap[staffKey] || 0) + Number(c.commission_amount || 0);
        }
      });

      let totalKomisiTerapis = 0; // KOTOR, sebelum potongan retensi 20%
      let totalRetensiDitahanBulanIni = 0;

      (therapists || [])
        .filter(t => t.is_active)
        .forEach(t => {
          const totalKomisi = komisiTerapisMap[(t.name || '').trim().toLowerCase()] || 0;
          const potonganRetensi = totalKomisi * 0.2;

          totalRetensiDitahanBulanIni += potonganRetensi;
          totalKomisiTerapis += totalKomisi;
        });

      setHutangPiutang({
        kasbon_diberikan: hpRow?.kasbon_diberikan || 0, 
        kasbon_dibayar: hpRow?.kasbon_dibayar || 0, 
        retensi_ditahan: totalRetensiDitahanBulanIni, 
        retensi_dicairkan: hpRow?.retensi_dicairkan || 0,
      });

      setManualStats({
        komisiTerapis: totalKomisiTerapis,
        komisiGro: totalKomisiGro
      });

      setLoading(false);
    };

    fetchReport();

    const sub = supabase.channel('financial-report-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => fetchReport())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bills' }, () => fetchReport())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'expenses' }, () => fetchReport())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payroll_commissions' }, () => fetchReport())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lembur_logs' }, () => fetchReport())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kasbon_transactions' }, () => fetchReport())
      .subscribe();

    return () => supabase.removeChannel(sub);
  }, [month, yearFilter, startDate, endDate]);

  const biayaItems = data ? [
    { label: 'Gaji Staff & Terapis', value: Number(data.biaya_gaji_staff_tetap) || 0, color: 'bg-purple-500' },
    { label: 'Lembur', value: Number(data.biaya_lembur) || 0, color: 'bg-orange-500' },
    { label: 'Komisi GRO & Terapis', value: manualStats.komisiGro + manualStats.komisiTerapis, color: 'bg-pink-500' },
    { label: 'Operasional', value: Number(data.biaya_operasional) || 0, color: 'bg-blue-500' },
    { label: 'Amortisasi', value: Number(data.amortisasi) || 0, color: 'bg-indigo-400' },
  ] : [];

  const totalBiayaAktual = biayaItems.reduce((sum, item) => sum + item.value, 0) || 0;
  const labaBersihAktual = data ? Number(data.laba_kotor) - totalBiayaAktual : 0;
  const marginAktual = data && Number(data.pendapatan_bersih) > 0 
    ? (labaBersihAktual / Number(data.pendapatan_bersih)) * 100 
    : 0;
    
  const isProfit = labaBersihAktual >= 0;
  const totalBiayaForBar = totalBiayaAktual || 1;
  const displayLabel = getCalendarDisplayLabel(startDate, endDate, month, yearFilter);

  return (
    <div className="space-y-4">
      <FilterBar 
        month={month} setMonth={setMonth} 
        year={yearFilter} setYear={setYearFilter} 
        startDate={startDate} setStartDate={setStartDate} 
        endDate={endDate} setEndDate={setEndDate} 
      />

      {loading ? (
        <div className="text-center py-12 text-gray-500">Memuat laporan...</div>
      ) : (
        <>
          <div className={`rounded-2xl p-6 text-white flex flex-col md:flex-row md:items-end md:justify-between gap-4 ${isProfit ? 'bg-gradient-to-br from-green-600 to-green-700' : 'bg-gradient-to-br from-red-600 to-red-700'}`}>
            <div>
              <p className="text-sm text-white/80 mb-1">Laba Bersih ({displayLabel})</p>
              <p className="text-4xl font-bold tracking-tight">Rp {labaBersihAktual.toLocaleString('id-ID')}</p>
            </div>
            <div className="flex items-center gap-2 bg-white/15 rounded-lg px-4 py-2 w-max">
              {isProfit ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
              <span className="text-lg font-semibold">{marginAktual.toLocaleString('id-ID', { maximumFractionDigits: 1 })}% margin</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Pendapatan</h3>
              <div className="space-y-0.5">
                <ReportRow label="Pendapatan Kotor" value={data.pendapatan_kotor} />
                <ReportRow label="Diskon" value={data.diskon} sign="-" muted />
                <ReportRow label="Pendapatan Bersih" value={data.pendapatan_bersih} strong divider />
                <ReportRow label="HPP / FnB" value={data.hpp_fnb} sign="-" muted />
                <ReportRow label="Laba Kotor" value={data.laba_kotor} strong divider />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Rincian Biaya</h3>
                <span className="text-sm font-bold text-gray-900">Rp {totalBiayaAktual.toLocaleString('id-ID')}</span>
              </div>
              <div className="space-y-3">
                {biayaItems.map(item => {
                  const pct = (item.value / totalBiayaForBar) * 100;
                  return (
                    <div key={item.label}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-medium text-gray-900">Rp {item.value.toLocaleString('id-ID')}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {hutangPiutang && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-600 mb-1">Hutang & Piutang</h3>
              <p className="text-xs text-gray-400 mb-4">Pergerakan kas ini tidak mempengaruhi Laba Bersih -- cuma pindah tangan, bukan biaya.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Piutang (Kasbon)</p>
                  <ReportRow label="Diberikan bulan ini" value={hutangPiutang.kasbon_diberikan} />
                  <ReportRow label="Dibayar/dipotong bulan ini" value={hutangPiutang.kasbon_dibayar} sign="-" muted />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hutang (Retensi Terapis)</p>
                  <ReportRow label="Ditahan bulan ini" value={hutangPiutang.retensi_ditahan} />
                  <ReportRow label="Dicairkan bulan ini" value={hutangPiutang.retensi_dicairkan} sign="-" muted />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-8 gap-y-2 bg-white rounded-lg shadow-sm border px-5 py-3 text-sm">
            <div><span className="text-gray-400">Jumlah Transaksi </span><span className="font-semibold text-gray-900">{Number(data.jumlah_transaksi).toLocaleString('id-ID')}</span></div>
            <div><span className="text-gray-400">Rata-rata / Transaksi </span><span className="font-semibold text-gray-900">Rp {Number(data.rata_rata_transaksi).toLocaleString('id-ID')}</span></div>
          </div>
        </>
      )}
    </div>
  );
}

function ReportRow({ label, value, sign, muted, strong, divider }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${divider ? 'border-t border-gray-100 pt-2 mt-1' : ''}`}>
      <span className={`text-sm ${strong ? 'font-semibold text-gray-900' : muted ? 'text-gray-400' : 'text-gray-600'}`}>{label}</span>
      <span className={`text-sm ${strong ? 'font-bold text-gray-900 text-base' : muted ? 'text-gray-400' : 'text-gray-800 font-medium'}`}>
        {sign === '-' ? '- ' : ''}Rp {Number(value || 0).toLocaleString('id-ID')}
      </span>
    </div>
  );
}

function SummaryCard({ label, value, highlight, isCurrency = true }) {
  return (
    <div className={`rounded-lg shadow-sm border p-4 ${highlight ? 'bg-gray-900 text-white' : 'bg-white'}`}>
      <p className={`text-xs mb-1 ${highlight ? 'text-gray-300' : 'text-gray-500'}`}>{label}</p>
      <p className="text-lg font-bold">{isCurrency ? `Rp ${Number(value).toLocaleString('id-ID')}` : Number(value).toLocaleString('id-ID')}</p>
    </div>
  );
}